/**
 * FM db-service — platform (P2.3 split).
 */
import bcrypt from "bcryptjs";
import { sql, nextId, todayIso, addDaysIso, addYearsIso } from "../db.js";
import type { SmAppUser, SmRole } from "@/domain/sm-models";
import {
  getSmLimitsForPlan,
  mergeSmPlanLimits,
  smLimitLabel,
  type SmPlanLimits,
  type SmLimitResource,
} from "../plan-limits.js";
import { rowValueToIsoDate, rowValueToIsoDateTime } from "../format-date.js";
import type { Partner, Referral } from "../partner-types.js";
import { assertMeteredWithPack, dbConsumePackCredit } from "../pack-economy.js";
import { workspaceRoleRowsFromOperationalPresets } from "@structura/core/operational";
import { rowToUser, rowToTenant, rowToTenantConfig } from "./row-mappers.js";
import { getPublicSiteUrl } from "../public-site.js";
import { materializeTenantProductFlagsForPlan } from "../tenant-product-flags.js";

// ─── AUTH ─────────────────────────────────────────────────────────────────────

export async function dbSmFindUserByEmail(email: string): Promise<SmAppUser | null> {
  const db = sql();
  const em = email.trim().toLowerCase();
  const rows = await db`
    SELECT id, email, username, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities, platform_role_template_id, avatar_url, theme_mode
    FROM sm_users WHERE LOWER(email) = ${em} LIMIT 1
  `;
  return rows.length ? rowToUser(rows[0]) : null;
}

/** Login por usuario o email (demos con mismo email de contacto). Prefiere username exacto. */
export async function dbSmFindUserByLogin(login: string): Promise<SmAppUser | null> {
  const db = sql();
  const id = login.trim().toLowerCase();
  if (!id) return null;
  const rows = await db`
    SELECT id, email, username, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities, platform_role_template_id, avatar_url, theme_mode
    FROM sm_users
    WHERE LOWER(username) = ${id} OR LOWER(email) = ${id}
    ORDER BY CASE WHEN LOWER(username) = ${id} THEN 0 ELSE 1 END, id ASC
    LIMIT 1
  `;
  return rows.length ? rowToUser(rows[0]) : null;
}

/** Candidatos de login (mismo email compartido en demos). */
export async function dbSmFindUsersByLogin(login: string): Promise<SmAppUser[]> {
  const db = sql();
  const id = login.trim().toLowerCase();
  if (!id) return [];
  const rows = await db`
    SELECT id, email, username, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities, platform_role_template_id, avatar_url, theme_mode
    FROM sm_users
    WHERE LOWER(username) = ${id} OR LOWER(email) = ${id}
    ORDER BY CASE WHEN LOWER(username) = ${id} THEN 0 ELSE 1 END, id ASC
  `;
  return rows.map((r) => rowToUser(r));
}

export async function dbSmUpsertUserByAuth0Sub(
  sub: string,
  email: string,
  name: string,
): Promise<SmAppUser | { error: string }> {
  const db = sql();
  const em = email.trim().toLowerCase();

  const approved = await db`SELECT email FROM sm_approved_emails WHERE email = ${em} LIMIT 1`;
  if (!approved.length) {
    const isAdmin = await db`SELECT id FROM sm_users WHERE role = 'super_admin' LIMIT 1`;
    if (isAdmin.length) {
      return {
        error:
          "Email no aprobado para registro. Contratá un plan en /contratar o pedí una invitación.",
      };
    }
  }

  const bySub = await db`
    SELECT id, email, username, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities, platform_role_template_id, avatar_url, theme_mode
    FROM sm_users WHERE auth0_sub = ${sub} LIMIT 1
  `;
  if (bySub.length) return rowToUser(bySub[0]);

  const byEmail = await db`
    SELECT id, email, username, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities, platform_role_template_id, avatar_url, theme_mode
    FROM sm_users WHERE LOWER(email) = ${em} LIMIT 1
  `;
  if (byEmail.length) {
    await db`UPDATE sm_users SET auth0_sub = ${sub} WHERE id = ${byEmail[0].id}`;
    return rowToUser(byEmail[0]);
  }

  const uid = nextId("u");
  await db`
    INSERT INTO sm_users (id, email, name, role, auth0_sub)
    VALUES (${uid}, ${em}, ${name}, 'owner', ${sub})
  `;
  return {
    id: uid,
    email: em,
    username: null,
    password: "",
    role: "owner",
    tenantId: null,
    name,
    active: true,
    staffPermissions: null,
    operationalRoleId: null,
    permissionOverrides: null,
    permissionCapabilities: null,
  };
}

export async function dbSmGetUserById(id: string): Promise<SmAppUser | null> {
  const db = sql();
  const rows = await db`
    SELECT id, email, username, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities, platform_role_template_id, avatar_url, theme_mode
    FROM sm_users WHERE id = ${id} LIMIT 1
  `;
  return rows.length ? rowToUser(rows[0]) : null;
}

export async function dbSmGetUiPreferences(userId: string): Promise<Record<string, unknown>> {
  const db = sql();
  const rows = await db`SELECT ui_preferences FROM sm_users WHERE id = ${userId} LIMIT 1`;
  if (!rows.length) return {};
  const raw = rows[0].ui_preferences;
  return raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
}

export async function dbSmPatchUiPreferences(
  userId: string,
  patch: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const current = await dbSmGetUiPreferences(userId);
  const merged = { ...current, ...patch };
  const db = sql();
  await db`UPDATE sm_users SET ui_preferences = ${JSON.stringify(merged)}::jsonb WHERE id = ${userId}`;
  return merged;
}

export async function dbSmVerifyPassword(user: SmAppUser, password: string): Promise<boolean> {
  if (!user.password || !user.password.startsWith("$2")) return false;
  return bcrypt.compare(password, user.password);
}

export async function dbSmRegisterUser(input: {
  email: string;
  password: string;
  name: string;
  businessName?: string;
}): Promise<SmAppUser | { error: string }> {
  const db = sql();
  const exists = await db`SELECT id FROM sm_users WHERE email = ${input.email} LIMIT 1`;
  if (exists.length) return { error: "Email ya registrado" };

  const approved = await db`SELECT email FROM sm_approved_emails WHERE email = ${input.email} LIMIT 1`;
  if (!approved.length) {
    const isAdmin = await db`SELECT id FROM sm_users WHERE role = 'super_admin' LIMIT 1`;
    if (isAdmin.length) {
      return {
        error:
          "Email no aprobado para registro. Contratá un plan en /contratar o pedí una invitación.",
      };
    }
  }

  const hash = await bcrypt.hash(input.password, 10);
  const id = nextId("u");
  await db`
    INSERT INTO sm_users (id, email, password, role, name)
    VALUES (${id}, ${input.email}, ${hash}, 'owner', ${input.name})
  `;
  return {
    id,
    email: input.email,
    username: null,
    password: hash,
    role: "owner",
    tenantId: null,
    name: input.name,
    active: true,
    staffPermissions: null,
    operationalRoleId: null,
    permissionOverrides: null,
    permissionCapabilities: null,
  };
}

export async function dbSmAddApprovedEmail(email: string): Promise<void> {
  const db = sql();
  await db`INSERT INTO sm_approved_emails (email) VALUES (${email}) ON CONFLICT DO NOTHING`;
}

export async function dbSmSetPasswordResetToken(userId: string, token: string, expires: Date): Promise<void> {
  const db = sql();
  await db`UPDATE sm_users SET password_reset_token = ${token}, password_reset_expires = ${expires.toISOString()} WHERE id = ${userId}`;
}

export async function dbSmFindUserByResetToken(token: string): Promise<SmAppUser | null> {
  const db = sql();
  const rows = await db`
    SELECT id, email, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities
    FROM sm_users
    WHERE password_reset_token = ${token} AND password_reset_expires > NOW()
    LIMIT 1
  `;
  return rows.length ? rowToUser(rows[0]) : null;
}

export async function dbSmConsumeResetToken(userId: string, hashedPassword: string): Promise<void> {
  const db = sql();
  await db`UPDATE sm_users SET password = ${hashedPassword}, password_reset_token = NULL, password_reset_expires = NULL WHERE id = ${userId}`;
}

/** Setea el password (ya hasheado) de un usuario. Limpia tokens de reset pendientes. */
export async function dbSmSetUserPassword(userId: string, hashedPassword: string): Promise<void> {
  const db = sql();
  await db`UPDATE sm_users SET password = ${hashedPassword}, password_reset_token = NULL, password_reset_expires = NULL WHERE id = ${userId}`;
}

export async function dbSmPatchUser(
  userId: string,
  patch: {
    name?: string;
    email?: string;
    role?: SmRole;
    tenantId?: string | null;
    active?: boolean;
    staffPermissions?: Record<string, boolean> | null;
    operationalRoleId?: string | null;
    permissionOverrides?: Record<string, boolean> | null;
    permissionCapabilities?: Record<string, string> | null;
    platformRoleTemplateId?: string | null;
  },
): Promise<{ ok: true } | { error: string }> {
  const db = sql();
  const em = patch.email?.trim().toLowerCase();
  if (em) {
    const existing = await dbSmFindUserByEmail(em);
    if (existing && existing.id !== userId) return { error: "Ya existe un usuario con ese email" };
    await db`UPDATE sm_users SET email = ${em} WHERE id = ${userId}`;
  }
  const nm = patch.name?.trim();
  if (nm) await db`UPDATE sm_users SET name = ${nm} WHERE id = ${userId}`;
  if (patch.role !== undefined) await db`UPDATE sm_users SET role = ${patch.role} WHERE id = ${userId}`;
  if (patch.tenantId !== undefined) await db`UPDATE sm_users SET tenant_id = ${patch.tenantId} WHERE id = ${userId}`;
  if (patch.active !== undefined) await db`UPDATE sm_users SET active = ${patch.active} WHERE id = ${userId}`;
  if (patch.staffPermissions !== undefined) {
    await db`UPDATE sm_users SET staff_permissions = ${patch.staffPermissions ? JSON.stringify(patch.staffPermissions) : null} WHERE id = ${userId}`;
  }
  if (patch.operationalRoleId !== undefined) {
    await db`UPDATE sm_users SET operational_role_id = ${patch.operationalRoleId} WHERE id = ${userId}`;
  }
  if (patch.permissionOverrides !== undefined) {
    await db`UPDATE sm_users SET permission_overrides = ${patch.permissionOverrides ? JSON.stringify(patch.permissionOverrides) : null} WHERE id = ${userId}`;
  }
  if (patch.permissionCapabilities !== undefined) {
    await db`UPDATE sm_users SET permission_capabilities = ${patch.permissionCapabilities ? JSON.stringify(patch.permissionCapabilities) : null} WHERE id = ${userId}`;
  }
  if (patch.platformRoleTemplateId !== undefined) {
    await db`UPDATE sm_users SET platform_role_template_id = ${patch.platformRoleTemplateId} WHERE id = ${userId}`;
  }
  return { ok: true };
}

export async function dbSmTouchUserLastAccess(userId: string): Promise<void> {
  try {
    const db = sql();
    await db`UPDATE sm_users SET last_access_at = NOW() WHERE id = ${userId}`;
  } catch {
    /* ignore */
  }
}

export async function dbSmDeleteUser(userId: string): Promise<void> {
  const db = sql();
  await db`DELETE FROM sm_users WHERE id = ${userId}`;
}

export interface ListUsersOpts {
  tenantId?: string;
  role?: string;
  /** Staff de plataforma (super_admin/support/viewer) vive en su propia pestaña — excluirlo del listado de usuarios de tenants por default. */
  excludePlatformStaff?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export async function dbSmListUsers(opts: ListUsersOpts = {}): Promise<SmAppUser[]> {
  const db = sql();
  const qLike = opts.q ? `%${opts.q.toLowerCase().trim()}%` : null;
  const limit = Math.min(opts.limit ?? 50, 200);
  const offset = opts.offset ?? 0;
  const platformRoles = ["super_admin", "support", "viewer"];
  try {
    const rows = await db`
      SELECT id, email, username, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities, platform_role_template_id, avatar_url, theme_mode, created_at, last_access_at
      FROM sm_users
      WHERE 1=1
        ${opts.tenantId ? db`AND tenant_id = ${opts.tenantId}` : db``}
        ${opts.role ? db`AND role = ${opts.role}` : opts.excludePlatformStaff ? db`AND NOT (role = ANY(${platformRoles}))` : db``}
        ${qLike ? db`AND (lower(email) LIKE ${qLike} OR lower(name) LIKE ${qLike})` : db``}
      ORDER BY created_at NULLS LAST, role DESC, name
      LIMIT ${limit} OFFSET ${offset}
    `;
    return rows.map(rowToUser);
  } catch {
    const rows = await db`
      SELECT id, email, username, password, role, tenant_id, name, active, staff_permissions, operational_role_id, permission_overrides, permission_capabilities, platform_role_template_id, avatar_url, theme_mode
      FROM sm_users
      WHERE 1=1
        ${opts.tenantId ? db`AND tenant_id = ${opts.tenantId}` : db``}
        ${opts.role ? db`AND role = ${opts.role}` : opts.excludePlatformStaff ? db`AND NOT (role = ANY(${platformRoles}))` : db``}
        ${qLike ? db`AND (lower(email) LIKE ${qLike} OR lower(name) LIKE ${qLike})` : db``}
      ORDER BY role DESC, name
      LIMIT ${limit} OFFSET ${offset}
    `;
    return rows.map(rowToUser);
  }
}

export async function dbSmListTenantUsers(tenantId: string): Promise<
  {
    id: string; email: string; name: string; role: string; active: boolean; username: string | null;
    operationalRoleId: string | null; permissionOverrides: Record<string, boolean> | null;
    permissionCapabilities: Record<string, string> | null;
    lastAccessAt: string | null;
  }[]
> {
  const db = sql();
  try {
    const rows = await db`
      SELECT id, email, username, name, role, COALESCE(active, true) AS active,
        operational_role_id, permission_overrides, permission_capabilities, last_access_at
      FROM sm_users
      WHERE tenant_id = ${tenantId}
      ORDER BY role DESC, name
    `;
    return rows.map((r) => ({
      id: String(r.id),
      email: String(r.email),
      username: (r.username as string | null) ?? null,
      name: String(r.name ?? ""),
      role: String(r.role),
      active: Boolean(r.active),
      operationalRoleId: (r.operational_role_id as string | null) ?? null,
      permissionOverrides: (r.permission_overrides as Record<string, boolean> | null) ?? null,
      permissionCapabilities: (r.permission_capabilities as Record<string, string> | null) ?? null,
      lastAccessAt: r.last_access_at != null ? rowValueToIsoDateTime(r.last_access_at) : null,
    }));
  } catch {
    const rows = await db`
      SELECT id, email, username, name, role, COALESCE(active, true) AS active,
        operational_role_id, permission_overrides, permission_capabilities
      FROM sm_users
      WHERE tenant_id = ${tenantId}
      ORDER BY role DESC, name
    `;
    return rows.map((r) => ({
      id: String(r.id),
      email: String(r.email),
      username: (r.username as string | null) ?? null,
      name: String(r.name ?? ""),
      role: String(r.role),
      active: Boolean(r.active),
      operationalRoleId: (r.operational_role_id as string | null) ?? null,
      permissionOverrides: (r.permission_overrides as Record<string, boolean> | null) ?? null,
      permissionCapabilities: (r.permission_capabilities as Record<string, string> | null) ?? null,
      lastAccessAt: null,
    }));
  }
}

/** Plazas activas (owner + staff). Inactivos no consumen asiento. */
export async function dbSmCountActiveTeamSeats(tenantId: string): Promise<number> {
  const db = sql();
  const r = await db`
    SELECT COUNT(*)::int AS n FROM sm_users
    WHERE tenant_id = ${tenantId} AND COALESCE(active, true) = true
  `;
  return Number(r[0]?.n ?? 0);
}

/** Usuarios staff con un rol operativo asignado (para impacto al editar plantilla). */
export async function dbSmCountUsersByOperationalRole(
  tenantId: string,
  operationalRoleId: string,
): Promise<number> {
  const db = sql();
  const r = await db`
    SELECT COUNT(*)::int AS n FROM sm_users
    WHERE tenant_id = ${tenantId}
      AND operational_role_id = ${operationalRoleId}
      AND role = 'staff'
  `;
  return Number(r[0]?.n ?? 0);
}

export async function dbSmCreateTeamMember(opts: {
  tenantId: string;
  email: string;
  name: string;
  password: string;
  username?: string;
  /** Rol operativo (cashier/kitchen/etc) — debe ser válido para el business_profile del tenant, ver /api/team. */
  operationalRoleId?: string | null;
}): Promise<{ id: string; email: string; name: string } | { error: string }> {
  const db = sql();
  const email = opts.email.trim().toLowerCase();
  const exists = await db`SELECT id FROM sm_users WHERE email = ${email} LIMIT 1`;
  if (exists.length) return { error: "Ya existe un usuario con ese email" };
  const hash = await bcrypt.hash(opts.password, 10);
  const id = nextId("u");
  const username = opts.username?.trim() || email.split("@")[0];
  await db`
    INSERT INTO sm_users (id, email, username, password, role, tenant_id, name, active, operational_role_id)
    VALUES (${id}, ${email}, ${username}, ${hash}, 'staff', ${opts.tenantId}, ${opts.name.trim()}, true, ${opts.operationalRoleId ?? null})
  `;
  return { id, email, name: opts.name.trim() };
}

/** Alta de usuario desde super admin (owner o staff) con contraseña temporal. */
export async function dbSmAdminCreateMember(opts: {
  tenantId: string;
  email: string;
  name: string;
  role: "owner" | "staff";
  password: string;
}): Promise<{ id: string; email: string; name: string; tempPassword: string } | { error: string }> {
  const db = sql();
  const email = opts.email.trim().toLowerCase();
  const exists = await db`SELECT id FROM sm_users WHERE email = ${email} LIMIT 1`;
  if (exists.length) return { error: "Ya existe un usuario con ese email" };
  const hash = await bcrypt.hash(opts.password, 10);
  const id = nextId("u");
  const username = email.split("@")[0];
  const role = opts.role === "owner" ? "owner" : "staff";
  await db`
    INSERT INTO sm_users (id, email, username, password, role, tenant_id, name, active)
    VALUES (${id}, ${email}, ${username}, ${hash}, ${role}, ${opts.tenantId}, ${opts.name.trim()}, true)
  `;
  return { id, email, name: opts.name.trim(), tempPassword: opts.password };
}

/** Alta de staff de plataforma (sin tenant): super_admin | support | viewer. */
export async function dbSmAdminCreatePlatformStaff(opts: {
  email: string;
  name: string;
  role: "super_admin" | "support" | "viewer";
  password: string;
}): Promise<{ id: string; email: string; name: string; role: string; tempPassword: string } | { error: string }> {
  const db = sql();
  const email = opts.email.trim().toLowerCase();
  const exists = await db`SELECT id FROM sm_users WHERE email = ${email} LIMIT 1`;
  if (exists.length) return { error: "Ya existe un usuario con ese email" };
  const hash = await bcrypt.hash(opts.password, 10);
  const id = nextId("u");
  const username = email.split("@")[0];
  const role = opts.role;
  // SCRUM-441/442: asigna el preset del sistema por defecto — el nuevo usuario
  // queda con un perfil editable desde el día 1, en vez de "sin perfil".
  await db`
    INSERT INTO sm_users (id, email, username, password, role, tenant_id, name, active, platform_role_template_id)
    VALUES (${id}, ${email}, ${username}, ${hash}, ${role}, null, ${opts.name.trim()}, true, ${"plt_" + role})
  `;
  return { id, email, name: opts.name.trim(), role, tempPassword: opts.password };
}

export async function dbSmRemoveTeamMember(
  userId: string,
  tenantId: string,
): Promise<{ ok: boolean; error?: string }> {
  const db = sql();
  const rows = await db`SELECT role FROM sm_users WHERE id = ${userId} AND tenant_id = ${tenantId} LIMIT 1`;
  if (!rows.length) return { ok: false, error: "Usuario no encontrado" };
  if (rows[0].role === "owner") return { ok: false, error: "No se puede eliminar al owner del workspace" };
  await db`DELETE FROM sm_users WHERE id = ${userId} AND tenant_id = ${tenantId}`;
  return { ok: true };
}

// ─── TENANTS ──────────────────────────────────────────────────────────────────

export async function dbSmGetTenant(id: string) {
  const db = sql();
  const rows = await db`SELECT * FROM sm_tenants WHERE id = ${id} LIMIT 1`;
  return rows[0] ? rowToTenant(rows[0]) : null;
}

export async function dbSmGetTenantBySlug(slug: string) {
  const db = sql();
  const rows = await db`SELECT * FROM sm_tenants WHERE slug = ${slug} LIMIT 1`;
  return rows[0] ? rowToTenant(rows[0]) : null;
}

/**
 * Resuelve el slug de un tenant por su subdominio.
 * Prioriza el campo `subdomain` dedicado; si no hay match, cae al slug
 * (tenants sin subdominio configurado resuelven por slug, como antes).
 */
export async function dbSmResolveSlugBySubdomain(subdomain: string): Promise<string | null> {
  try {
    const { canonicalStorefrontSlug } = await import("@/lib/storefront-slug-aliases");
    const lookup = canonicalStorefrontSlug(subdomain);
    const db = sql();
    const rows = await db`
      SELECT slug FROM sm_tenants
      WHERE subdomain = ${lookup} OR (subdomain IS NULL AND slug = ${lookup})
         OR subdomain = ${subdomain} OR (subdomain IS NULL AND slug = ${subdomain})
      ORDER BY (subdomain = ${lookup}) DESC, (slug = ${lookup}) DESC
      LIMIT 1
    `;
    if (!rows[0]) return null;
    return canonicalStorefrontSlug(String(rows[0].slug));
  } catch {
    return null;
  }
}

/** Resuelve el slug de un tenant por su dominio propio. */
export async function dbSmResolveSlugByCustomDomain(domain: string): Promise<string | null> {
  try {
    const db = sql();
    const rows = await db`SELECT slug FROM sm_tenants WHERE custom_domain = ${domain} LIMIT 1`;
    return rows[0] ? String(rows[0].slug) : null;
  } catch {
    return null;
  }
}

export async function dbSmGetTenantConfig(tenantId: string) {
  const db = sql();
  const rows = await db`SELECT * FROM sm_tenant_config WHERE tenant_id = ${tenantId} LIMIT 1`;
  return rows[0] ? rowToTenantConfig(rows[0]) : null;
}

export async function dbSmCreateTenant(input: {
  name: string; email: string; phone?: string; ownerId?: string;
  planId?: string; validUntil?: string; trialDays?: number; trialSource?: string;
  referralCodeUsed?: string;
}) {
  const db = sql();
  const id = nextId("t");
  const baseSlug = input.name
    .toLowerCase().normalize("NFD").replace(/\p{M}/gu, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

  const existing = await db`SELECT 1 FROM sm_tenants WHERE slug = ${baseSlug} LIMIT 1`;
  const slug = existing.length > 0 ? baseSlug.slice(0, 54) + "-" + id.slice(-5) : baseSlug;

  if (input.trialDays) {
    const trialEndsAt = addDaysIso(input.trialDays);
    await db`
      INSERT INTO sm_tenants (id, name, slug, email, phone, trial_ends_at, trial_source, referral_code_used)
      VALUES (${id}, ${input.name}, ${slug}, ${input.email}, ${input.phone ?? null}, ${trialEndsAt}, ${input.trialSource ?? "self_signup"}, ${input.referralCodeUsed ?? null})
    `;
    await db`
      INSERT INTO sm_subscriptions (tenant_id, plan_id, status, valid_from, valid_until)
      VALUES (${id}, 'free_trial', 'active', ${todayIso()}, ${trialEndsAt})
    `;
  } else {
    await db`
      INSERT INTO sm_tenants (id, name, slug, email, phone, referral_code_used)
      VALUES (${id}, ${input.name}, ${slug}, ${input.email}, ${input.phone ?? null}, ${input.referralCodeUsed ?? null})
    `;
    const planId = input.planId ?? "pro";
    const validUntil = input.validUntil ?? addYearsIso(1);
    await db`
      INSERT INTO sm_subscriptions (tenant_id, plan_id, status, valid_from, valid_until)
      VALUES (${id}, ${planId}, 'active', ${todayIso()}, ${validUntil})
    `;
  }

  // Crear config por defecto
  await db`INSERT INTO sm_tenant_config (tenant_id) VALUES (${id}) ON CONFLICT DO NOTHING`;

  // Entitlements from plan (GA-15): capability resolver reads the tenant snapshot,
  // not the live template. Without this, a new `pro` tenant keeps DEFAULT flags
  // (ai_assistant=false) even though the plan template includes it.
  const assignedPlanId = input.trialDays ? "free_trial" : (input.planId ?? "pro");
  const flags = materializeTenantProductFlagsForPlan(assignedPlanId, null);
  await db`UPDATE sm_tenants SET product_flags = ${JSON.stringify(flags)}::jsonb, updated_at = NOW() WHERE id = ${id}`;

  // Wave experience: preset por perfil (o catalog_default). Sin published → storefront legacy.
  try {
    const { publishSeededTenantExperienceFromDb } = await import(
      "../experience-definition/publish-seeded-experience.js"
    );
    await publishSeededTenantExperienceFromDb(db, id, {
      demoSlug: slug,
      onlyIfMissing: true,
    });
  } catch {
    // best-effort: el tenant queda usable; se puede publicar experience después
  }

  if (input.ownerId) {
    await db`UPDATE sm_users SET tenant_id = ${id} WHERE id = ${input.ownerId}`;
  }

  return { id, slug };
}

export async function dbSmLinkTenantByOwnerEmail(email: string, userId: string): Promise<string | null> {
  const db = sql();
  const rows = await db`
    SELECT t.id FROM sm_tenants t
    WHERE t.email = ${email}
      AND NOT EXISTS (SELECT 1 FROM sm_users u WHERE u.tenant_id = t.id)
    LIMIT 1
  `;
  if (!rows.length) return null;
  const tenantId = String(rows[0].id);
  await db`UPDATE sm_users SET tenant_id = ${tenantId} WHERE id = ${userId}`;
  return tenantId;
}

export async function dbSmPatchTenant(id: string, patch: Record<string, unknown>) {
  const db = sql();
  const has = (k: string) => k in patch && patch[k] !== undefined;
  const v = (k: string) => patch[k] as string;
  await Promise.all([
    has("name")                 && db`UPDATE sm_tenants SET name = ${v("name")}, updated_at = NOW() WHERE id = ${id}`,
    has("slug")                 && db`UPDATE sm_tenants SET slug = ${v("slug")}, updated_at = NOW() WHERE id = ${id}`,
    has("email")                && db`UPDATE sm_tenants SET email = ${v("email")}, updated_at = NOW() WHERE id = ${id}`,
    has("phone")                && db`UPDATE sm_tenants SET phone = ${v("phone")}, updated_at = NOW() WHERE id = ${id}`,
    has("address")              && db`UPDATE sm_tenants SET address = ${v("address")}, updated_at = NOW() WHERE id = ${id}`,
    has("description")          && db`UPDATE sm_tenants SET description = ${v("description")}, updated_at = NOW() WHERE id = ${id}`,
    has("logo_url")             && db`UPDATE sm_tenants SET logo_url = ${v("logo_url")}, updated_at = NOW() WHERE id = ${id}`,
    has("lifecycle_status")     && db`UPDATE sm_tenants SET lifecycle_status = ${v("lifecycle_status")}, updated_at = NOW() WHERE id = ${id}`,
    has("account_status")       && db`UPDATE sm_tenants SET account_status = ${v("account_status")}, updated_at = NOW() WHERE id = ${id}`,
    has("onboarding_step")      && db`UPDATE sm_tenants SET onboarding_step = ${patch["onboarding_step"] as number}, updated_at = NOW() WHERE id = ${id}`,
    has("onboarding_complete")  && db`UPDATE sm_tenants SET onboarding_complete = ${patch["onboarding_complete"] as boolean}, updated_at = NOW() WHERE id = ${id}`,
    has("onboarding_drafts")    && db`UPDATE sm_tenants SET onboarding_drafts = ${JSON.stringify(patch["onboarding_drafts"])}, updated_at = NOW() WHERE id = ${id}`,
    has("product_flags")        && db`UPDATE sm_tenants SET product_flags = ${JSON.stringify(patch["product_flags"])}, updated_at = NOW() WHERE id = ${id}`,
    has("has_custom_theme")     && db`UPDATE sm_tenants SET has_custom_theme = ${patch["has_custom_theme"] as boolean}, updated_at = NOW() WHERE id = ${id}`,
    has("custom_theme_slug")    && db`UPDATE sm_tenants SET custom_theme_slug = ${v("custom_theme_slug")}, updated_at = NOW() WHERE id = ${id}`,
    has("subdomain")            && db`UPDATE sm_tenants SET subdomain = ${patch["subdomain"] === null ? null : v("subdomain")}, updated_at = NOW() WHERE id = ${id}`,
    has("custom_domain")        && db`UPDATE sm_tenants SET custom_domain = ${patch["custom_domain"] === null ? null : v("custom_domain")}, updated_at = NOW() WHERE id = ${id}`,
    has("custom_domain_status") && db`UPDATE sm_tenants SET custom_domain_status = ${v("custom_domain_status")}, updated_at = NOW() WHERE id = ${id}`,
    has("custom_domain_verified_at") && db`UPDATE sm_tenants SET custom_domain_verified_at = ${patch["custom_domain_verified_at"] as string | null}::timestamptz, updated_at = NOW() WHERE id = ${id}`,
    has("custom_domain_last_check_at") && db`UPDATE sm_tenants SET custom_domain_last_check_at = ${patch["custom_domain_last_check_at"] as string | null}::timestamptz, updated_at = NOW() WHERE id = ${id}`,
    has("custom_domain_check_error") && db`UPDATE sm_tenants SET custom_domain_check_error = ${patch["custom_domain_check_error"] as string | null}, updated_at = NOW() WHERE id = ${id}`,
    has("social_links")         && db`UPDATE sm_tenants SET social_links = ${JSON.stringify(patch["social_links"])}, updated_at = NOW() WHERE id = ${id}`,
    has("limits_override")      && db`UPDATE sm_tenants SET limits_override = ${patch["limits_override"] === null ? null : JSON.stringify(patch["limits_override"])}::jsonb, updated_at = NOW() WHERE id = ${id}`,
    has("feature_overrides")    && db`UPDATE sm_tenants SET feature_overrides = ${JSON.stringify(patch["feature_overrides"])}::jsonb, updated_at = NOW() WHERE id = ${id}`,
    has("analytics_metric_overrides") && db`UPDATE sm_tenants SET analytics_metric_overrides = ${JSON.stringify(patch["analytics_metric_overrides"])}::jsonb, updated_at = NOW() WHERE id = ${id}`,
    has("email_template_gate_overrides") && db`UPDATE sm_tenants SET email_template_gate_overrides = ${JSON.stringify(patch["email_template_gate_overrides"])}::jsonb, updated_at = NOW() WHERE id = ${id}`,
  ].filter(Boolean));
}

/** Cuenta assets con URL de imagen (productos, categorías, marcas). */
export async function dbSmCountTenantImages(tenantId: string): Promise<number> {
  const db = sql();
  const rows = await db`
    SELECT (
      (SELECT COUNT(*)::int FROM sm_products WHERE tenant_id = ${tenantId} AND activo = true AND COALESCE(imagen_url, '') <> '')
      + (SELECT COUNT(*)::int FROM sm_categories WHERE tenant_id = ${tenantId} AND COALESCE(image_url, '') <> '')
    ) AS n
  `;
  return Number(rows[0]?.n ?? 0);
}

export async function dbSmPatchTenantConfig(tenantId: string, patch: Record<string, unknown>, actorUserId?: string | null, skipAutoVersion = false) {
  const db = sql();
  const has = (k: string) => k in patch && patch[k] !== undefined;
  const v = (k: string) => patch[k] as string;
  // Import dinámico: node:crypto (vía secret-crypto.ts) rompe el bundle de cliente
  // si este módulo lo importa estático — db-service es alcanzable desde el cliente
  // vía sm-session.ts/use-session.ts.
  const encryptedWaCredentials = has("wa_credentials")
    ? (await import("../whatsapp")).encryptWaCredentials(patch["wa_credentials"] as Record<string, string> | null)
    : null;
  await Promise.all([
    has("default_tax_category")      && db`UPDATE sm_tenant_config SET default_tax_category = ${patch["default_tax_category"] as string | null}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("default_tax_rate")          && db`UPDATE sm_tenant_config SET default_tax_rate = ${patch["default_tax_rate"] as number | null}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("default_price_mode")        && db`UPDATE sm_tenant_config SET default_price_mode = ${patch["default_price_mode"] as string | null}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("price_rounding")            && db`UPDATE sm_tenant_config SET price_rounding = ${patch["price_rounding"] as string}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("primary_color")             && db`UPDATE sm_tenant_config SET primary_color = ${v("primary_color")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("secondary_color")           && db`UPDATE sm_tenant_config SET secondary_color = ${v("secondary_color")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("background_color")          && db`UPDATE sm_tenant_config SET background_color = ${v("background_color")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("hero_image_url")            && db`UPDATE sm_tenant_config SET hero_image_url = ${v("hero_image_url")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("hero_video_url")            && db`UPDATE sm_tenant_config SET hero_video_url = ${v("hero_video_url")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("hero_title")                && db`UPDATE sm_tenant_config SET hero_title = ${v("hero_title")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("hero_subtitle")             && db`UPDATE sm_tenant_config SET hero_subtitle = ${v("hero_subtitle")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("categorias_activas")        && db`UPDATE sm_tenant_config SET categorias_activas = ${JSON.stringify(patch["categorias_activas"])}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("carruseles_activos")        && db`UPDATE sm_tenant_config SET carruseles_activos = ${JSON.stringify(patch["carruseles_activos"])}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("custom_product_tags")       && db`UPDATE sm_tenant_config SET custom_product_tags = ${JSON.stringify(patch["custom_product_tags"])}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("metodo_entrega")            && db`UPDATE sm_tenant_config SET metodo_entrega = ${v("metodo_entrega")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("radio_delivery_km")         && db`UPDATE sm_tenant_config SET radio_delivery_km = ${patch["radio_delivery_km"] as number}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("delivery_zones_enabled")    && db`UPDATE sm_tenant_config SET delivery_zones_enabled = ${patch["delivery_zones_enabled"] as boolean}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("delivery_distance_tiers")   && db`UPDATE sm_tenant_config SET delivery_distance_tiers = ${JSON.stringify(patch["delivery_distance_tiers"] ?? [])}::jsonb, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("whatsapp_number")           && db`UPDATE sm_tenant_config SET whatsapp_number = ${v("whatsapp_number")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("whatsapp_message_template") && db`UPDATE sm_tenant_config SET whatsapp_message_template = ${v("whatsapp_message_template")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("mp_access_token")           && db`UPDATE sm_tenant_config SET mp_access_token = ${v("mp_access_token")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("mp_public_key")             && db`UPDATE sm_tenant_config SET mp_public_key = ${v("mp_public_key")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("resend_api_key")            && db`UPDATE sm_tenant_config SET resend_api_key = ${v("resend_api_key")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("resend_from_email")         && db`UPDATE sm_tenant_config SET resend_from_email = ${v("resend_from_email")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("owner_email_notifications") && db`UPDATE sm_tenant_config SET owner_email_notifications = ${patch["owner_email_notifications"] as boolean}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("payment_methods")           && db`UPDATE sm_tenant_config SET payment_methods = ${JSON.stringify(patch["payment_methods"])}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("bank_transfer_holder")      && db`UPDATE sm_tenant_config SET bank_transfer_holder = ${v("bank_transfer_holder")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("bank_transfer_cbu")         && db`UPDATE sm_tenant_config SET bank_transfer_cbu = ${v("bank_transfer_cbu")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("bank_transfer_alias")       && db`UPDATE sm_tenant_config SET bank_transfer_alias = ${v("bank_transfer_alias")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("bank_transfer_bank")        && db`UPDATE sm_tenant_config SET bank_transfer_bank = ${v("bank_transfer_bank")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("bank_transfer_notes")       && db`UPDATE sm_tenant_config SET bank_transfer_notes = ${v("bank_transfer_notes")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("seo_meta_title")            && db`UPDATE sm_tenant_config SET seo_meta_title = ${v("seo_meta_title")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("seo_meta_description")      && db`UPDATE sm_tenant_config SET seo_meta_description = ${v("seo_meta_description")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("seo_og_image_url")          && db`UPDATE sm_tenant_config SET seo_og_image_url = ${v("seo_og_image_url")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("institutional_order_deadline_hour") && db`UPDATE sm_tenant_config SET institutional_order_deadline_hour = ${patch["institutional_order_deadline_hour"] as number}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("institutional_cutoff_hours")        && db`UPDATE sm_tenant_config SET institutional_cutoff_hours = ${patch["institutional_cutoff_hours"] as number}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("storefront_timezone")               && db`UPDATE sm_tenant_config SET storefront_timezone = ${v("storefront_timezone")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("business_profile")                  && db`UPDATE sm_tenant_config SET business_profile = ${v("business_profile")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("enabled_features")                  && db`UPDATE sm_tenant_config SET enabled_features = ${JSON.stringify(patch["enabled_features"])}::jsonb, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("tracking")                  && db`UPDATE sm_tenant_config SET tracking = ${JSON.stringify(patch["tracking"])}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("wa_provider")               && db`UPDATE sm_tenant_config SET wa_provider = ${v("wa_provider")}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("wa_credentials")            && db`UPDATE sm_tenant_config SET wa_credentials = ${JSON.stringify(encryptedWaCredentials)}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("wa_order_confirm_enabled")  && db`UPDATE sm_tenant_config SET wa_order_confirm_enabled = ${patch["wa_order_confirm_enabled"] as boolean}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("store_policies")            && db`UPDATE sm_tenant_config SET store_policies = ${JSON.stringify(patch["store_policies"])}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("payway_enabled")            && db`UPDATE sm_tenant_config SET payway_enabled = ${patch["payway_enabled"] as boolean}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("email_automations")         && db`UPDATE sm_tenant_config SET email_automations = ${JSON.stringify(patch["email_automations"])}::jsonb, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("experience_draft")          && db`UPDATE sm_tenant_config SET experience_draft = ${JSON.stringify(patch["experience_draft"])}::jsonb, experience_updated_at = NOW(), updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("experience_published")      && db`UPDATE sm_tenant_config SET experience_published = ${JSON.stringify(patch["experience_published"])}::jsonb, experience_published_at = NOW(), updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("hardware_bindings")         && db`UPDATE sm_tenant_config SET hardware_bindings = ${JSON.stringify(patch["hardware_bindings"])}::jsonb, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("show_address_on_storefront") && db`UPDATE sm_tenant_config SET show_address_on_storefront = ${patch["show_address_on_storefront"] as boolean}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("online_price_list_id") && db`UPDATE sm_tenant_config SET online_price_list_id = ${patch["online_price_list_id"] as string | null}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
    has("pos_price_list_id") && db`UPDATE sm_tenant_config SET pos_price_list_id = ${patch["pos_price_list_id"] as string | null}, updated_at = NOW() WHERE tenant_id = ${tenantId}`,
  ].filter(Boolean));

  // RBAC por nicho: al fijar/cambiar el rubro, (re)provisionar las plantillas de rol
  // operativo (cashier/kitchen/etc) para ese perfil — mismo path para self-service
  // onboarding y para cualquier alta manual/demo que pase por acá (no un sistema paralelo).
  if (has("business_profile")) {
    try {
      await dbSmProvisionOperationalRoleTemplates(tenantId, "fm", v("business_profile"));
    } catch {
      // best-effort: el tenant queda usable, los roles se pueden provisionar después desde AdminRolesPanel
    }
  }

  // SCRUM-320 — versionado automático: fire-and-forget, nunca bloquea ni falla el patch.
  // skipAutoVersion=true cuando el caller (ej. dbSmRestoreConfigSnapshotDomains) ya va a
  // crear su propia versión explícita con origin="restore" — evita un duplicado real
  // detectado en verificación en vivo (cada restore generaba versión "restore" + "auto").
  if (!skipAutoVersion) {
    void dbSmCreateAutoConfigVersion({ tenantId, changedColumns: Object.keys(patch), actorUserId: actorUserId ?? null });
  }

  return dbSmGetTenantConfig(tenantId);
}

export async function dbSmPatchTenantPublicStatus(
  tenantId: string,
  input: {
    status: "online" | "offline";
    actorUserId: string;
    offlineReason?: string | null;
  },
): Promise<{ previous: string; next: string }> {
  const db = sql();
  const prevRows = await db`
    SELECT tenant_public_status FROM sm_tenant_config WHERE tenant_id = ${tenantId} LIMIT 1
  `;
  const previous = String(prevRows[0]?.tenant_public_status ?? "online");
  const next = input.status;
  await db`
    UPDATE sm_tenant_config SET
      tenant_public_status = ${next},
      offline_reason = ${input.offlineReason ?? null},
      offline_updated_at = NOW(),
      offline_updated_by = ${input.actorUserId},
      updated_at = NOW()
    WHERE tenant_id = ${tenantId}
  `;
  return { previous, next };
}

/** Fusiona claves de tracking sin borrar widget u otros scripts ya guardados. */
export async function dbSmMergeTenantConfigTracking(
  tenantId: string,
  partial: Record<string, unknown>,
) {
  const current = await dbSmGetTenantConfig(tenantId);
  const merged = { ...(current?.tracking ?? {}), ...partial };
  return dbSmPatchTenantConfig(tenantId, { tracking: merged });
}

/**
 * Meta CAPI — token cifrado (SCRUM-115). Columna dedicada, fuera de `tracking`
 * (que se devuelve tal cual al cliente) — nunca debe llegar al browser.
 */
export async function dbSmGetMetaCapiTokenEncrypted(tenantId: string): Promise<string | null> {
  const db = sql();
  const rows = await db`SELECT meta_capi_token_encrypted FROM sm_tenant_config WHERE tenant_id = ${tenantId} LIMIT 1`;
  return (rows[0]?.meta_capi_token_encrypted as string | null) ?? null;
}

export async function dbSmSetMetaCapiTokenEncrypted(
  tenantId: string,
  encryptedTokenOrNull: string | null,
): Promise<void> {
  const db = sql();
  await db`UPDATE sm_tenant_config SET meta_capi_token_encrypted = ${encryptedTokenOrNull}, updated_at = now() WHERE tenant_id = ${tenantId}`;
}

/**
 * TikTok Events API — token cifrado (SCRUM-104). Columna dedicada, fuera de
 * `tracking` (que ya expone `tiktokPixelId` público al cliente) — el token
 * nunca debe llegar al browser.
 */
export async function dbSmGetTiktokEventsTokenEncrypted(tenantId: string): Promise<string | null> {
  const db = sql();
  const rows = await db`SELECT tiktok_events_token_encrypted FROM sm_tenant_config WHERE tenant_id = ${tenantId} LIMIT 1`;
  return (rows[0]?.tiktok_events_token_encrypted as string | null) ?? null;
}

export async function dbSmSetTiktokEventsTokenEncrypted(
  tenantId: string,
  encryptedTokenOrNull: string | null,
): Promise<void> {
  const db = sql();
  await db`UPDATE sm_tenant_config SET tiktok_events_token_encrypted = ${encryptedTokenOrNull}, updated_at = now() WHERE tenant_id = ${tenantId}`;
}

/**
 * Google Merchant API — OAuth por tenant (SCRUM-95). Tokens en columnas
 * dedicadas cifradas (mismo esquema que Meta CAPI), metadata de conexión
 * (status, accountId, dataSource, último sync) en `google_merchant_connection`
 * — ninguna de las tres pasa por rowToTenantConfig (allowlist explícito), así
 * que nunca llegan al cliente vía GET /api/tenant/config.
 */
export interface GoogleMerchantConnection {
  status: "connected" | "disconnected" | "needs_reconnect";
  merchantAccountId: string | null;
  dataSourceName: string | null;
  accountLabel: string | null;
  connectedAt: string | null;
  updatedAt: string | null;
  lastSyncAt: string | null;
  lastSyncOk: boolean | null;
  lastSyncError: string | null;
  lastSyncCount: number | null;
  /** Expiración del access token OAuth vigente — server-only, para decidir cuándo refrescar. */
  tokenExpiresAt: string | null;
}

export async function dbSmGetGoogleMerchantTokens(tenantId: string): Promise<{
  accessTokenEncrypted: string | null;
  refreshTokenEncrypted: string | null;
  connection: GoogleMerchantConnection | null;
}> {
  const db = sql();
  const rows = await db`
    SELECT google_merchant_access_token_encrypted, google_merchant_refresh_token_encrypted, google_merchant_connection
    FROM sm_tenant_config WHERE tenant_id = ${tenantId} LIMIT 1
  `;
  const row = rows[0];
  return {
    accessTokenEncrypted: (row?.google_merchant_access_token_encrypted as string | null) ?? null,
    refreshTokenEncrypted: (row?.google_merchant_refresh_token_encrypted as string | null) ?? null,
    connection: (row?.google_merchant_connection as GoogleMerchantConnection | null) ?? null,
  };
}

export async function dbSmSetGoogleMerchantConnection(
  tenantId: string,
  patch: {
    accessTokenEncrypted?: string | null;
    refreshTokenEncrypted?: string | null;
    connection?: Partial<GoogleMerchantConnection>;
  },
): Promise<void> {
  const db = sql();
  const current = await dbSmGetGoogleMerchantTokens(tenantId);
  const nextConnection = patch.connection
    ? { ...(current.connection ?? {}), ...patch.connection }
    : current.connection;
  const nextAccess = patch.accessTokenEncrypted !== undefined ? patch.accessTokenEncrypted : current.accessTokenEncrypted;
  const nextRefresh = patch.refreshTokenEncrypted !== undefined ? patch.refreshTokenEncrypted : current.refreshTokenEncrypted;
  await db`
    UPDATE sm_tenant_config
    SET google_merchant_access_token_encrypted = ${nextAccess},
        google_merchant_refresh_token_encrypted = ${nextRefresh},
        google_merchant_connection = ${nextConnection ? JSON.stringify(nextConnection) : null},
        updated_at = now()
    WHERE tenant_id = ${tenantId}
  `;
}

/**
 * Tablas con `tenant_id` sin FK a `sm_tenants` (no se borran por CASCADE).
 * Mantener alineado con el inventario de information_schema.
 */
export const SM_TENANT_ORPHAN_TABLES = [
  "sm_audit_logs",
  "sm_business_events",
  "sm_combos",
  "sm_email_events",
  "sm_help_analytics",
  "sm_invitations",
  "sm_merchant_quotes",
  "sm_merchant_tenant_config",
  "sm_onboarding_sessions",
  "sm_payment_events",
  "sm_platform_errors",
  "sm_platform_events",
  "sm_subscription_events",
] as const;

export type SmTenantPurgeResult = {
  deleted: boolean;
  tenantId: string;
  slug: string | null;
  name: string | null;
  subdomain: string | null;
  customDomain: string | null;
  isDemo: boolean;
  r2Prefix: string;
  orphanDeletes: Record<string, number>;
};

/**
 * Purge completo de workspace: tablas huérfanas, referrals, leads/onboarding
 * ligados por slug, desvincula users, y DELETE sm_tenants (CASCADE ~80 FKs).
 * DNS/R2 los limpia el caller con el snapshot devuelto.
 */
export async function dbSmDeleteTenant(tenantId: string): Promise<SmTenantPurgeResult> {
  const db = sql();
  const tenant = await dbSmGetTenant(tenantId);
  if (!tenant) {
    return {
      deleted: false,
      tenantId,
      slug: null,
      name: null,
      subdomain: null,
      customDomain: null,
      isDemo: false,
      r2Prefix: `tenants/${tenantId}/`,
      orphanDeletes: {},
    };
  }

  const slug = tenant.slug ?? null;
  const orphanDeletes: Record<string, number> = {};

  const bump = async (table: string, rows: { count?: number }[] | unknown[]) => {
    const n = Array.isArray(rows) ? rows.length : 0;
    orphanDeletes[table] = (orphanDeletes[table] ?? 0) + n;
  };

  // Eventos / analytics / merchant / invitations (sin FK)
  {
    const r = await db`DELETE FROM sm_audit_logs WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_audit_logs", r);
  }
  {
    const r = await db`
      DELETE FROM sm_business_events
      WHERE tenant_id = ${tenantId} OR workspace_id = ${tenantId}
      RETURNING id
    `;
    await bump("sm_business_events", r);
  }
  {
    const r = await db`DELETE FROM sm_combos WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_combos", r);
  }
  {
    const r = await db`DELETE FROM sm_email_events WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_email_events", r);
  }
  {
    const r = await db`DELETE FROM sm_help_analytics WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_help_analytics", r);
  }
  {
    const r = await db`DELETE FROM sm_invitations WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_invitations", r);
  }
  {
    const r = await db`DELETE FROM sm_merchant_quotes WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_merchant_quotes", r);
  }
  {
    // PK = tenant_id (sin columna id)
    const r = await db`
      DELETE FROM sm_merchant_tenant_config WHERE tenant_id = ${tenantId} RETURNING tenant_id
    `;
    await bump("sm_merchant_tenant_config", r);
  }
  {
    const r = await db`DELETE FROM sm_payment_events WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_payment_events", r);
  }
  {
    const r = await db`
      DELETE FROM sm_platform_errors
      WHERE tenant_id = ${tenantId} OR workspace_id = ${tenantId}
      RETURNING id
    `;
    await bump("sm_platform_errors", r);
  }
  {
    const r = await db`DELETE FROM sm_platform_events WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_platform_events", r);
  }
  {
    const r = await db`DELETE FROM sm_subscription_events WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_subscription_events", r);
  }

  // Onboarding: por tenant_id y por business_slug
  {
    const r = slug
      ? await db`
          DELETE FROM sm_onboarding_sessions
          WHERE tenant_id = ${tenantId} OR business_slug = ${slug}
          RETURNING id
        `
      : await db`
          DELETE FROM sm_onboarding_sessions WHERE tenant_id = ${tenantId} RETURNING id
        `;
    await bump("sm_onboarding_sessions", r);
  }

  // Referrals: FK es SET NULL — borrar fila para no dejar rastro del workspace
  {
    const r = await db`DELETE FROM sm_referrals WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_referrals", r);
  }

  // Leads comerciales ligados al workspace (form_data o nombre/slug)
  {
    const name = tenant.name ?? null;
    const r =
      slug && name
        ? await db`
            DELETE FROM sm_leads
            WHERE form_data->>'tenantId' = ${tenantId}
               OR form_data->>'businessSlug' = ${slug}
               OR lower(negocio) = lower(${slug})
               OR (status = 'approved' AND lower(negocio) = lower(${name}))
            RETURNING id
          `
        : slug
          ? await db`
              DELETE FROM sm_leads
              WHERE form_data->>'tenantId' = ${tenantId}
                 OR form_data->>'businessSlug' = ${slug}
                 OR lower(negocio) = lower(${slug})
              RETURNING id
            `
          : await db`
              DELETE FROM sm_leads WHERE form_data->>'tenantId' = ${tenantId} RETURNING id
            `;
    await bump("sm_leads", r);
  }

  // SCRUM-204: sm_legal_acceptances tiene ON DELETE RESTRICT — borrar antes del tenant.
  {
    const r = await db`DELETE FROM sm_legal_acceptances WHERE tenant_id = ${tenantId} RETURNING id`;
    await bump("sm_legal_acceptances", r);
  }

  // Cuentas de plataforma: desvincular (no borrar el usuario)
  await db`UPDATE sm_users SET tenant_id = NULL WHERE tenant_id = ${tenantId}`;

  await db`DELETE FROM sm_tenants WHERE id = ${tenantId}`;

  return {
    deleted: true,
    tenantId,
    slug,
    name: tenant.name ?? null,
    subdomain: tenant.subdomain || slug,
    customDomain: tenant.customDomain ?? null,
    isDemo: Boolean(tenant.productFlags?.is_demo),
    r2Prefix: `tenants/${tenantId}/`,
    orphanDeletes,
  };
}

export async function dbSmListTenants() {
  const db = sql();
  const rows = await db`
    SELECT t.*, s.plan_id, s.status as sub_status, s.valid_until,
           tc.business_profile AS list_business_profile
    FROM sm_tenants t
    LEFT JOIN LATERAL (
      SELECT plan_id, status, valid_until
      FROM sm_subscriptions
      WHERE tenant_id = t.id
      ORDER BY created_at DESC
      LIMIT 1
    ) s ON true
    LEFT JOIN sm_tenant_config tc ON tc.tenant_id = t.id
    ORDER BY t.created_at DESC
  `;
  return rows.map((r) => ({
    ...rowToTenant(r),
    planId: r.plan_id as string | null,
    subStatus: r.sub_status as string | null,
    validUntil: r.valid_until ? String(r.valid_until).slice(0, 10) : null,
    businessProfile: (r.list_business_profile as string | null) ?? "catalog",
  }));
}

export async function dbSmGetSubscription(tenantId: string) {
  const db = sql();
  const rows = await db`SELECT * FROM sm_subscriptions WHERE tenant_id = ${tenantId} ORDER BY created_at DESC LIMIT 1`;
  return rows[0] ?? null;
}

// ─── DEMO RESET (como Booking Hub) ───────────────────────────────────────────

export async function dbSmResetDemoTenant(
  tenantId: string,
): Promise<{ orders: number; institutionalOrders: number }> {
  const tenant = await dbSmGetTenant(tenantId);
  if (!tenant?.productFlags?.is_demo) {
    throw new Error("El tenant especificado no es un sandbox de demo");
  }
  const db = sql();
  await db`
    DELETE FROM sm_order_items
    WHERE order_id IN (SELECT id FROM sm_orders WHERE tenant_id = ${tenantId})
  `;
  const iRows = await db`
    DELETE FROM sm_institutional_orders WHERE tenant_id = ${tenantId} RETURNING id
  `;
  const oRows = await db`DELETE FROM sm_orders WHERE tenant_id = ${tenantId} RETURNING id`;
  await db`
    UPDATE sm_tenants
    SET lifecycle_status = 'active', account_status = 'active', updated_at = NOW()
    WHERE id = ${tenantId}
  `;
  return { orders: oRows.length, institutionalOrders: iRows.length };
}

// ── Tenant operational check ──────────────────────────────────────────────────

export async function dbSmAssertTenantOperational(
  tenantId: string,
): Promise<{ ok: true } | { ok: false; message: string; code: string }> {
  const tenant = await dbSmGetTenant(tenantId);
  if (!tenant) return { ok: false, message: "Tenant no encontrado", code: "not_found" };
  if (tenant.productFlags?.is_demo) return { ok: true };

  const sub = await dbSmGetSubscription(tenantId);
  const today = todayIso();
  const hasValidSub = sub && sub.status === "active" && (
    () => { const until = String(sub.valid_until).slice(0, 10); return until >= today; }
  )();

  if (hasValidSub) {
    if (tenant.lifecycleStatus === "paused") {
      const db = sql();
      await db`UPDATE sm_tenants SET lifecycle_status = 'active', updated_at = NOW() WHERE id = ${tenantId}`;
    }
    return { ok: true };
  }

  if (tenant.trialEndsAt && new Date(tenant.trialEndsAt) < new Date()) {
    const db = sql();
    if (tenant.lifecycleStatus !== "paused") {
      await db`UPDATE sm_tenants SET lifecycle_status = 'paused', updated_at = NOW() WHERE id = ${tenantId}`;
    }
    return { ok: false, code: "TRIAL_EXPIRED", message: "Tu período de prueba expiró. Solicitá una invitación o renovación." };
  }

  if (!sub) return { ok: false, message: "Sin suscripción activa", code: "no_subscription" };
  if (sub.status !== "active") return { ok: false, message: "Suscripción inactiva", code: "subscription_inactive" };
  const until = String(sub.valid_until).slice(0, 10);
  if (until < today) return { ok: false, message: "Suscripción vencida", code: "subscription_expired" };
  return { ok: true };
}

export async function dbSmGetPlanLimitsForPlan(planId: string | null | undefined): Promise<SmPlanLimits> {
  if (!planId) return getSmLimitsForPlan(null);
  const base = getSmLimitsForPlan(planId);
  const plan = await dbSmGetPlan(planId);
  // El registro de sm_plans en DB es un override PARCIAL sobre la definición
  // completa del plan (igual que limits_override a nivel tenant, ver
  // dbSmGetPlanLimitsForTenant) — no la fuente de verdad exclusiva. Si se
  // tratara como fuente exclusiva, cualquier flag booleano ausente en esa
  // fila (ej. inventory_bom, gift_cards) se perdía silenciosamente para
  // TODOS los tenants del plan, sin importar lo que diga la definición
  // canónica del plan en código.
  if (plan?.limits) return mergeSmPlanLimits(base, plan.limits as unknown as Record<string, unknown>);
  return base;
}

/**
 * Límites efectivos del tenant (SCRUM-192):
 * plan activo → niche limits_override (business_profile) → tenant limits_override.
 */
export async function dbSmGetPlanLimitsForTenant(tenantId: string): Promise<SmPlanLimits> {
  const db = sql();
  const subRows = await db`
    SELECT plan_id FROM sm_subscriptions WHERE tenant_id = ${tenantId} AND status = 'active'
    ORDER BY created_at DESC LIMIT 1
  `;
  const planId = subRows[0] ? String(subRows[0].plan_id) : null;
  const base = await dbSmGetPlanLimitsForPlan(planId);
  try {
    const { mergePlanLimitsWithNicheAndTenant } = await import("@/lib/plan-limits.js");
    const { profileToNiche } = await import("@/lib/renata/copilot-features.js");

    const tRows = await db`
      SELECT t.limits_override, tc.business_profile
      FROM sm_tenants t
      LEFT JOIN sm_tenant_config tc ON tc.tenant_id = t.id
      WHERE t.id = ${tenantId}
      LIMIT 1
    `;
    const tenantOv = tRows[0]?.limits_override as Record<string, unknown> | null | undefined;
    const nicheId = profileToNiche(String(tRows[0]?.business_profile ?? "catalog"));

    let nicheOv: Record<string, unknown> | null = null;
    if (planId && nicheId) {
      try {
        const nRows = await db`
          SELECT limits_override FROM sm_niche_plan_marketing
          WHERE niche_id = ${nicheId} AND plan_id = ${planId}
          LIMIT 1
        `;
        nicheOv = (nRows[0]?.limits_override as Record<string, unknown> | null | undefined) ?? null;
      } catch {
        nicheOv = null;
      }
    }

    return mergePlanLimitsWithNicheAndTenant(base, nicheOv, tenantOv);
  } catch {
    return base;
  }
}

/** Features booleanas del plan (no cuotas). */
export type SmPlanFeatureFlag =
  | "variants_addons" | "customer_accounts" | "loyalty_points" | "gift_cards"
  | "digital_products"
  | "storefront" | "portal_b2b"
  | "payment_pos_mode" | "inventory_bom" | "dine_in_tables" | "table_reservations" | "supply_chain"
  | "discount_coupons" | "promotions_engine";

const PLAN_FEATURE_LABEL: Record<SmPlanFeatureFlag, string> = {
  variants_addons: "Variantes y extras",
  customer_accounts: "Cuentas de clientes",
  loyalty_points: "Programa de puntos",
  gift_cards: "Gift cards",
  digital_products: "Productos digitales",
  storefront: "Tienda pública",
  portal_b2b: "B2B / Mayorista",
  payment_pos_mode: "POS / Punto de venta",
  inventory_bom: "Escandallos / BOM",
  dine_in_tables: "Mesas QR — Salón",
  table_reservations: "Reservas de mesa",
  supply_chain: "Supply Chain",
  discount_coupons: "Cupones de descuento",
  promotions_engine: "Promociones",
};

export async function dbSmAssertPlanFeature(
  tenantId: string,
  feature: SmPlanFeatureFlag,
): Promise<{ ok: true } | { ok: false; message: string; code: string }> {
  // Unificado vía facade (plan path bit-igual). Dual keys pueden usar either en callers HTTP.
  const { resolveTenantCapability, defaultCapabilityPath } = await import("@/lib/tenant-capability");
  const path = defaultCapabilityPath(feature) === "either" ? "either" : "plan";
  const resolved = await resolveTenantCapability(tenantId, feature, { path });
  if (resolved.enabled) return { ok: true };
  return {
    ok: false,
    code: "plan_feature_locked",
    message: `${PLAN_FEATURE_LABEL[feature]} no está disponible en tu plan actual. Actualizá tu plan para activarla.`,
  };
}

export async function dbSmAssertPlanLimit(
  tenantId: string,
  resource: SmLimitResource,
): Promise<{ ok: true; usesPack?: boolean } | { ok: false; message: string; code: string; limit: number; current: number }> {
  const db = sql();
  const subRows = await db`SELECT plan_id FROM sm_subscriptions WHERE tenant_id = ${tenantId} AND status = 'active' ORDER BY created_at DESC LIMIT 1`;
  const planId = subRows[0] ? String(subRows[0].plan_id) : null;
  const limits = await dbSmGetPlanLimitsForTenant(tenantId);
  const limit = limits[resource];
  if (limit === null) return { ok: true };

  let current = 0;
  if (resource === "products") {
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_products WHERE tenant_id = ${tenantId} AND activo = true`;
    current = Number(r[0]?.n ?? 0);
  } else if (resource === "locations") {
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_locations WHERE tenant_id = ${tenantId} AND active = true`;
    current = Number(r[0]?.n ?? 0);
  } else if (resource === "categories") {
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_categories WHERE tenant_id = ${tenantId}`;
    current = Number(r[0]?.n ?? 0);
  } else if (resource === "brands") {
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_brands WHERE tenant_id = ${tenantId}`;
    current = Number(r[0]?.n ?? 0);
  } else if (resource === "team_members") {
    // Owner + staff activos — asiento por tenant (distinto de b2b_users, que son compradores institucionales).
    // Comprable sin cambiar de plan vía pack "team_member" (mismo mecanismo que b2b_users/images/orders_month).
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_users WHERE tenant_id = ${tenantId} AND COALESCE(active, true) = true`;
    current = Number(r[0]?.n ?? 0);
    const metered = await assertMeteredWithPack({
      tenantId,
      current,
      planLimit: limit,
      packType: "team_member",
      resourceLabel: "miembros del equipo",
    });
    if (!metered.ok) {
      return {
        ok: false,
        code: metered.code,
        message: metered.message,
        limit: metered.limit,
        current: metered.current,
      };
    }
    return { ok: true, usesPack: metered.usesPack };
  } else if (resource === "b2b_users") {
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_institutional_users WHERE tenant_id = ${tenantId}`;
    current = Number(r[0]?.n ?? 0);
    const metered = await assertMeteredWithPack({
      tenantId,
      current,
      planLimit: limit,
      packType: "b2b",
      resourceLabel: "usuarios B2B",
    });
    if (!metered.ok) {
      return {
        ok: false,
        code: metered.code,
        message: metered.message,
        limit: metered.limit,
        current: metered.current,
      };
    }
    return { ok: true, usesPack: metered.usesPack };
  } else if (resource === "images") {
    current = await dbSmCountTenantImages(tenantId);
    const metered = await assertMeteredWithPack({
      tenantId,
      current,
      planLimit: limit,
      packType: "image",
      resourceLabel: "imágenes en catálogo",
    });
    if (!metered.ok) {
      return {
        ok: false,
        code: metered.code,
        message: metered.message,
        limit: metered.limit,
        current: metered.current,
      };
    }
    return { ok: true, usesPack: metered.usesPack };
  } else if (resource === "professionals") {
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_professionals WHERE tenant_id = ${tenantId} AND active = true`;
    current = Number(r[0]?.n ?? 0);
  } else if (resource === "bookings_per_month") {
    const firstOfMonth = todayIso().slice(0, 8) + "01";
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_bookings WHERE tenant_id = ${tenantId} AND created_at >= ${firstOfMonth}`;
    current = Number(r[0]?.n ?? 0);
  } else if (resource === "orders_month") {
    const firstOfMonth = todayIso().slice(0, 8) + "01";
    const r = await db`SELECT COUNT(*)::int AS n FROM sm_orders WHERE tenant_id = ${tenantId} AND created_at >= ${firstOfMonth}`;
    current = Number(r[0]?.n ?? 0);
    const metered = await assertMeteredWithPack({
      tenantId,
      current,
      planLimit: limit,
      packType: "order",
      resourceLabel: "pedidos mensuales",
    });
    if (!metered.ok) {
      return {
        ok: false,
        code: metered.code,
        message: metered.message,
        limit: metered.limit,
        current: metered.current,
      };
    }
    return { ok: true, usesPack: metered.usesPack };
  }

  if (current >= limit) {
    return {
      ok: false,
      code: "plan_limit_reached",
      message: `Tu plan permite hasta ${limit} ${smLimitLabel(resource)}. Actualizá tu plan para continuar.`,
      limit,
      current,
    };
  }
  return { ok: true };
}

/** Valida cupo antes de subir imagen de catálogo (producto/categoría). `replacing` = cambiar URL existente. */
export async function dbSmAssertImageUpload(
  tenantId: string,
  kind: "product" | "category",
  replacing: boolean,
): Promise<{ ok: true; usesPack?: boolean } | { ok: false; message: string; code: string }> {
  if (replacing) return { ok: true };
  const limits = await dbSmGetPlanLimitsForTenant(tenantId);
  if (limits.images === null) return { ok: true };
  const current = await dbSmCountTenantImages(tenantId);
  const metered = await assertMeteredWithPack({
    tenantId,
    current,
    planLimit: limits.images,
    packType: "image",
    resourceLabel: kind === "product" ? "imágenes de productos" : "imágenes de categorías",
  });
  if (!metered.ok) {
    return { ok: false, code: metered.code, message: metered.message };
  }
  return { ok: true, usesPack: metered.usesPack };
}

/** Tras persistir una imagen nueva en catálogo, descuenta pack si superó el tope del plan. */
export async function dbSmConsumeImagePackIfOverLimit(tenantId: string): Promise<void> {
  const limits = await dbSmGetPlanLimitsForTenant(tenantId);
  if (limits.images === null) return;
  const current = await dbSmCountTenantImages(tenantId);
  if (current > limits.images) {
    await dbConsumePackCredit(tenantId, "image", 1);
  }
}

// ─── LEADS ────────────────────────────────────────────────────────────────────

export async function dbSmCreateLead(input: {
  nombre: string; negocio: string; email: string; telefono: string;
  planInteresado: string; formData?: Record<string, unknown>;
}) {
  const db = sql();
  const id = nextId("lead");
  const formDataJson = input.formData ? JSON.stringify(input.formData) : null;
  await db`
    INSERT INTO sm_leads (id, nombre, negocio, email, telefono, plan_interesado, form_data)
    VALUES (${id}, ${input.nombre}, ${input.negocio}, ${input.email}, ${input.telefono}, ${input.planInteresado}, ${formDataJson}::jsonb)
  `;
  return id;
}

export interface ListLeadsOpts {
  status?: string;
  plan?: string;
  q?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

/** Lista unificada: leads manuales + self-service sincronizados, con el paso del wizard cuando aplica. */
export async function dbSmListLeads(opts: ListLeadsOpts = {}) {
  const db = sql();
  const qLike = opts.q ? `%${opts.q.toLowerCase().trim()}%` : null;
  const limit = Math.min(opts.limit ?? 200, 500);
  const offset = opts.offset ?? 0;

  return db`
    SELECT
      l.*,
      os.status AS onboarding_status,
      os.id AS onboarding_session_id
    FROM sm_leads l
    LEFT JOIN sm_onboarding_sessions os
      ON os.id = (l.form_data->>'onboardingSessionId')
    WHERE 1=1
      ${opts.status ? db`AND l.status = ${opts.status}` : db``}
      ${opts.plan ? db`AND l.plan_interesado = ${opts.plan}` : db``}
      ${opts.from ? db`AND l.created_at >= ${opts.from}` : db``}
      ${opts.to ? db`AND l.created_at <= ${opts.to}` : db``}
      ${qLike ? db`AND (lower(l.email) LIKE ${qLike} OR lower(l.nombre) LIKE ${qLike} OR lower(l.negocio) LIKE ${qLike})` : db``}
    ORDER BY l.created_at DESC
    LIMIT ${limit} OFFSET ${offset}
  `;
}

export async function dbSmGetLead(leadId: string) {
  const db = sql();
  const rows = await db`SELECT * FROM sm_leads WHERE id = ${leadId} LIMIT 1`;
  return rows[0] ?? null;
}

export async function dbSmApproveLead(
  leadId: string,
  approvedBy: string,
  link?: { tenantId?: string; businessSlug?: string },
): Promise<void> {
  const db = sql();
  if (link?.tenantId || link?.businessSlug) {
    await db`
      UPDATE sm_leads SET
        approved_at = NOW(),
        approved_by = ${approvedBy},
        status = 'approved',
        form_data = COALESCE(form_data, '{}'::jsonb)
          || ${JSON.stringify({
            ...(link.tenantId ? { tenantId: link.tenantId } : {}),
            ...(link.businessSlug ? { businessSlug: link.businessSlug } : {}),
          })}::jsonb
      WHERE id = ${leadId}
    `;
    return;
  }
  await db`UPDATE sm_leads SET approved_at = NOW(), approved_by = ${approvedBy}, status = 'approved' WHERE id = ${leadId}`;
}

export async function dbSmUpsertSubscription(
  tenantId: string,
  input: { planId: string; status: string; validFrom: string; validUntil: string },
): Promise<void> {
  const db = sql();
  const existing = await db`
    SELECT id FROM sm_subscriptions WHERE tenant_id = ${tenantId} ORDER BY created_at DESC LIMIT 1
  `;
  if (existing.length) {
    await db`
      UPDATE sm_subscriptions
      SET plan_id = ${input.planId}, status = ${input.status},
          valid_from = ${input.validFrom}, valid_until = ${input.validUntil}
      WHERE id = ${existing[0].id}
    `;
  } else {
    await db`
      INSERT INTO sm_subscriptions (tenant_id, plan_id, status, valid_from, valid_until)
      VALUES (${tenantId}, ${input.planId}, ${input.status}, ${input.validFrom}, ${input.validUntil})
    `;
  }
}

export async function dbSmGetStats() {
  const db = sql();
  const today = todayIso();
  const thirtyAgo = new Date();
  thirtyAgo.setDate(thirtyAgo.getDate() - 30);
  const thirtyAgoIso = thirtyAgo.toISOString().slice(0, 10);

  const [
    tenants,
    users,
    leadsPending,
    approvalsPending,
    orders,
    ordersToday,
    products,
    activeTenants,
    demoTenants,
    ordersByTenantRows,
    ordersTodayByTenantRows,
    planRows,
    leadsByStatusRows,
    ordersPerDayRows,
    ordersByHourRows,
  ] = await Promise.all([
    db`SELECT COUNT(*)::int AS n FROM sm_tenants`,
    db`SELECT COUNT(*)::int AS n FROM sm_users WHERE role != 'super_admin'`,
    db`SELECT COUNT(*)::int AS n FROM sm_leads WHERE status = 'pending'`,
    // Negocio › Por aprobar: transferencias por acreditar + pruebas gratis esperando aprobación.
    db`SELECT COUNT(*)::int AS n FROM sm_onboarding_sessions
       WHERE (status = 'payment_pending' AND payment_provider ILIKE '%transfer%') OR status = 'pending_approval'`,
    db`SELECT COUNT(*)::int AS n FROM sm_orders`,
    db`SELECT COUNT(*)::int AS n FROM sm_orders WHERE created_at::date = ${today}::date`,
    db`SELECT COUNT(*)::int AS n FROM sm_products WHERE activo = true`,
    db`SELECT COUNT(*)::int AS n FROM sm_tenants WHERE lifecycle_status = 'active'`,
    db`SELECT COUNT(*)::int AS n FROM sm_tenants WHERE COALESCE(product_flags->>'is_demo', 'false') = 'true'`,
    db`SELECT tenant_id, COUNT(*)::int AS n FROM sm_orders GROUP BY tenant_id`,
    db`SELECT tenant_id, COUNT(*)::int AS n FROM sm_orders WHERE created_at::date = ${today}::date GROUP BY tenant_id`,
    db`SELECT plan_id, COUNT(*)::int AS n FROM sm_subscriptions WHERE status = 'active' GROUP BY plan_id`,
    db`SELECT status, COUNT(*)::int AS n FROM sm_leads GROUP BY status`,
    db`
      SELECT created_at::date AS d, COUNT(*)::int AS n
      FROM sm_orders
      WHERE created_at::date >= ${thirtyAgoIso}::date
      GROUP BY created_at::date
      ORDER BY d ASC
    `,
    db`
      SELECT EXTRACT(HOUR FROM created_at)::int AS h, COUNT(*)::int AS n
      FROM sm_orders
      GROUP BY EXTRACT(HOUR FROM created_at)
      ORDER BY h ASC
    `,
  ]);

  const byTenant: Record<string, number> = {};
  for (const r of ordersByTenantRows as { tenant_id: string; n: number }[]) {
    byTenant[String(r.tenant_id)] = Number(r.n);
  }

  const ordersTodayByTenant: Record<string, number> = {};
  for (const r of ordersTodayByTenantRows as { tenant_id: string; n: number }[]) {
    ordersTodayByTenant[String(r.tenant_id)] = Number(r.n);
  }

  const planBreakdown: Record<string, number> = {};
  for (const r of planRows as { plan_id: string; n: number }[]) {
    planBreakdown[String(r.plan_id)] = Number(r.n);
  }

  const leadsByStatus: Record<string, number> = {};
  for (const r of leadsByStatusRows as { status: string; n: number }[]) {
    leadsByStatus[String(r.status)] = Number(r.n);
  }

  const ordersPerDay30 = (ordersPerDayRows as { d: unknown; n: number }[]).map((r) => ({
    date: rowValueToIsoDate(r.d),
    count: Number(r.n),
  })).filter((r) => r.date);

  const ordersByHour: { hour: number; count: number }[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    count: 0,
  }));
  for (const r of ordersByHourRows as { h: number; n: number }[]) {
    const h = Number(r.h);
    if (h >= 0 && h < 24) ordersByHour[h].count = Number(r.n);
  }

  const todayStr = today;
  const churn30Rows = await db`
    SELECT COUNT(*)::int AS n FROM sm_subscriptions
    WHERE valid_until < ${todayStr}::date AND status != 'active'
  `;
  const churn30 = Number(churn30Rows[0]?.n ?? 0);

  return {
    tenants: Number(tenants[0]?.n ?? 0),
    users: Number(users[0]?.n ?? 0),
    leadsPending: Number(leadsPending[0]?.n ?? 0),
    approvalsPending: Number(approvalsPending[0]?.n ?? 0),
    ordersTotal: Number(orders[0]?.n ?? 0),
    ordersToday: Number(ordersToday[0]?.n ?? 0),
    productsTotal: Number(products[0]?.n ?? 0),
    activeTenants: Number(activeTenants[0]?.n ?? 0),
    demoTenants: Number(demoTenants[0]?.n ?? 0),
    byTenant,
    ordersTodayByTenant,
    planBreakdown,
    leadsByStatus,
    ordersPerDay30,
    ordersByHour,
    churn30,
  };
}

export async function dbSmRejectLead(leadId: string, reason: string): Promise<void> {
  const db = sql();
  await db`UPDATE sm_leads SET rejected_at = NOW(), rejected_reason = ${reason}, status = 'rejected' WHERE id = ${leadId}`;
}

// ─── INVITACIONES ─────────────────────────────────────────────────────────────

export async function dbSmListInvitations() {
  const db = sql();
  const rows = await db`SELECT * FROM sm_invitations ORDER BY created_at DESC`;
  return rows;
}

export async function dbSmCreateInvitation(input: {
  email: string; businessName?: string; invitedBy: string; trialDays: number; token?: string; notes?: string;
}) {
  const db = sql();
  const id = nextId("inv");
  const token = input.token ?? nextId("tok");
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + 7);
  await db`
    INSERT INTO sm_invitations (id, email, business_name, invited_by, trial_days, token, notes, expires_at)
    VALUES (${id}, ${input.email}, ${input.businessName ?? null}, ${input.invitedBy}, ${input.trialDays}, ${token}, ${input.notes ?? null}, ${expiresAt.toISOString()})
  `;
  return { id, token };
}

export async function dbSmGetInvitationByToken(token: string): Promise<{
  status: string; expiresAt: string; businessName: string; email: string;
  trialDays: number | undefined; invitedBy: string | undefined; notes: string | undefined;
} | null> {
  const db = sql();
  const rows = await db`SELECT * FROM sm_invitations WHERE token = ${token} AND expires_at > NOW() AND status = 'pending' LIMIT 1`;
  if (!rows[0]) return null;
  const r = rows[0];
  return {
    status: String(r.status ?? "pending"),
    expiresAt: String(r.expires_at ?? r.expiresAt ?? ""),
    businessName: String(r.business_name ?? r.businessName ?? ""),
    email: String(r.email ?? ""),
    trialDays: r.trial_days != null ? Number(r.trial_days) : undefined,
    invitedBy: r.invited_by != null ? String(r.invited_by) : undefined,
    notes: r.notes != null ? String(r.notes) : undefined,
  };
}

export async function dbSmAcceptInvitation(token: string, tenantId: string): Promise<void> {
  const db = sql();
  await db`UPDATE sm_invitations SET status = 'accepted', accepted_at = NOW(), tenant_id = ${tenantId} WHERE token = ${token}`;
}

// ─── PLATFORM SETTINGS ────────────────────────────────────────────────────────

export async function dbSmGetPlatformResendApiKey(): Promise<string | null> {
  try {
    const db = sql();
    const rows = await db`SELECT resend_api_key FROM sm_platform_settings WHERE id = 'default' LIMIT 1`;
    if (!rows.length) return null;
    const k = rows[0].resend_api_key;
    return k != null && String(k).trim() ? String(k).trim() : null;
  } catch {
    return null;
  }
}

export async function dbSmSetPlatformResendApiKey(apiKey: string | null): Promise<void> {
  const db = sql();
  await db`
    INSERT INTO sm_platform_settings (id, resend_api_key, updated_at)
    VALUES ('default', ${apiKey}, NOW())
    ON CONFLICT (id) DO UPDATE SET resend_api_key = EXCLUDED.resend_api_key, updated_at = NOW()
  `;
}

export async function dbSmGetQzPrivateKey(): Promise<string | null> {
  try {
    const db = sql();
    const rows = await db`SELECT qz_private_key FROM sm_platform_settings WHERE id = 'default' LIMIT 1`;
    if (!rows.length) return null;
    const k = rows[0].qz_private_key;
    return k != null && String(k).trim() ? String(k).trim() : null;
  } catch {
    return null;
  }
}

export async function dbSmSetQzPrivateKey(b64Key: string | null): Promise<void> {
  const db = sql();
  await db`
    INSERT INTO sm_platform_settings (id, qz_private_key, updated_at)
    VALUES ('default', ${b64Key}, NOW())
    ON CONFLICT (id) DO UPDATE SET qz_private_key = EXCLUDED.qz_private_key, updated_at = NOW()
  `;
}

// ─── PLATFORM FLAGS (registry global) ─────────────────────────────────────────
// Persistidos en sm_platform_settings.product_flags (JSONB). Control SaaS super_admin.

export type SmPlatformFlagKey = "onboarding_online_enabled" | "pwa_enabled";
const SM_PLATFORM_FLAG_DEFAULTS: Record<SmPlatformFlagKey, boolean> = {
  onboarding_online_enabled: true,
  pwa_enabled: true,
};

export async function dbSmGetPlatformFlags(): Promise<Record<SmPlatformFlagKey, boolean>> {
  try {
    const db = sql();
    const rows = await db`SELECT product_flags FROM sm_platform_settings WHERE id = 'default' LIMIT 1`;
    const raw = (rows[0]?.product_flags as Record<string, unknown> | null) ?? {};
    const bool = (k: SmPlatformFlagKey) =>
      typeof raw[k] === "boolean" ? (raw[k] as boolean) : SM_PLATFORM_FLAG_DEFAULTS[k];
    return {
      onboarding_online_enabled: bool("onboarding_online_enabled"),
      pwa_enabled: bool("pwa_enabled"),
    };
  } catch {
    return { ...SM_PLATFORM_FLAG_DEFAULTS };
  }
}

export async function dbSmSetPlatformFlag(key: SmPlatformFlagKey, value: boolean): Promise<void> {
  const db = sql();
  const rows = await db`SELECT product_flags FROM sm_platform_settings WHERE id = 'default' LIMIT 1`;
  const raw = (rows[0]?.product_flags as Record<string, unknown> | null) ?? {};
  const next = { ...raw, [key]: value };
  await db`
    INSERT INTO sm_platform_settings (id, product_flags, updated_at)
    VALUES ('default', ${JSON.stringify(next)}, NOW())
    ON CONFLICT (id) DO UPDATE SET product_flags = EXCLUDED.product_flags, updated_at = NOW()
  `;
}

// ─── FEATURE OVERRIDES (lifecycle/enabled global) ─────────────────────────────
// Guardados en sm_platform_settings.product_flags.feature_overrides preservando
// el resto del JSONB. Permiten prender/apagar features y cambiar su lifecycle sin redeploy.

export type SmFeatureLifecycleOverride = "stable" | "beta" | "upcoming" | "deprecated";
export interface SmFeatureOverride {
  enabled?: boolean;
  lifecycle?: SmFeatureLifecycleOverride;
}

async function smReadPlatformProductFlags(): Promise<Record<string, unknown>> {
  try {
    const db = sql();
    const rows = await db`SELECT product_flags FROM sm_platform_settings WHERE id = 'default' LIMIT 1`;
    return (rows[0]?.product_flags as Record<string, unknown> | null) ?? {};
  } catch {
    return {};
  }
}

export async function dbSmGetFeatureOverrides(): Promise<Record<string, SmFeatureOverride>> {
  const raw = await smReadPlatformProductFlags();
  const ov = raw.feature_overrides;
  return ov && typeof ov === "object" ? (ov as Record<string, SmFeatureOverride>) : {};
}

/** Defaults de permisos del rol staff (plantilla a nivel plataforma). */
export async function dbSmGetStaffPermissionDefaults(): Promise<Record<string, boolean> | null> {
  const raw = await smReadPlatformProductFlags();
  const v = raw.staff_permission_defaults;
  return v && typeof v === "object" ? (v as Record<string, boolean>) : null;
}

export async function dbSmSetStaffPermissionDefaults(defaults: Record<string, boolean>): Promise<void> {
  const db = sql();
  const raw = await smReadPlatformProductFlags();
  const next = { ...raw, staff_permission_defaults: defaults };
  await db`
    INSERT INTO sm_platform_settings (id, product_flags, updated_at)
    VALUES ('default', ${JSON.stringify(next)}, NOW())
    ON CONFLICT (id) DO UPDATE SET product_flags = EXCLUDED.product_flags, updated_at = NOW()
  `;
}

export async function dbSmSetFeatureOverride(
  featureId: string,
  patch: SmFeatureOverride,
): Promise<Record<string, SmFeatureOverride>> {
  const db = sql();
  const raw = await smReadPlatformProductFlags();
  const current = raw.feature_overrides && typeof raw.feature_overrides === "object"
    ? (raw.feature_overrides as Record<string, SmFeatureOverride>)
    : {};
  const merged: Record<string, SmFeatureOverride> = {
    ...current,
    [featureId]: { ...current[featureId], ...patch },
  };
  const next = { ...raw, feature_overrides: merged };
  await db`
    INSERT INTO sm_platform_settings (id, product_flags, updated_at)
    VALUES ('default', ${JSON.stringify(next)}, NOW())
    ON CONFLICT (id) DO UPDATE SET product_flags = EXCLUDED.product_flags, updated_at = NOW()
  `;
  return merged;
}

// ─── FEATURE ADMIN METADATA (operacional — no runtime) ────────────────────────

export interface SmFeatureAdminMetaPatch {
  operationalDomain?: string;
  featureType?: string;
  maturityLevel?: string;
  recommendedPlanFloor?: string;
  upgradeDriver?: boolean | null;
  requiresSetup?: boolean | null;
  requiresProvider?: boolean | null;
  hiddenFromMarketing?: boolean | null;
  requires?: string[];
  aliases?: string[];
  tags?: string[];
  notes?: string | null;
}

function rowToFeatureAdminMetaPatch(row: Record<string, unknown>): SmFeatureAdminMetaPatch {
  const patch: SmFeatureAdminMetaPatch = {};
  if (typeof row.operational_domain === "string") patch.operationalDomain = row.operational_domain;
  if (typeof row.feature_type === "string") patch.featureType = row.feature_type;
  if (typeof row.maturity_level === "string") patch.maturityLevel = row.maturity_level;
  if (typeof row.recommended_plan_floor === "string") {
    patch.recommendedPlanFloor = row.recommended_plan_floor;
  }
  if (typeof row.upgrade_driver === "boolean") patch.upgradeDriver = row.upgrade_driver;
  if (typeof row.requires_setup === "boolean") patch.requiresSetup = row.requires_setup;
  if (typeof row.requires_provider === "boolean") patch.requiresProvider = row.requires_provider;
  if (typeof row.hidden_from_marketing === "boolean") {
    patch.hiddenFromMarketing = row.hidden_from_marketing;
  }
  if (Array.isArray(row.requires)) patch.requires = row.requires as string[];
  if (Array.isArray(row.aliases)) patch.aliases = row.aliases as string[];
  if (Array.isArray(row.tags)) patch.tags = row.tags as string[];
  if (typeof row.notes === "string") patch.notes = row.notes;
  return patch;
}

export async function dbSmListFeatureAdminMeta(): Promise<Record<string, SmFeatureAdminMetaPatch>> {
  try {
    const db = sql();
    const rows = await db`
      SELECT feature_id, operational_domain, feature_type, maturity_level,
             recommended_plan_floor, upgrade_driver, requires_setup, requires_provider,
             hidden_from_marketing, requires, aliases, tags, notes
      FROM sm_feature_admin_meta
    `;
    const out: Record<string, SmFeatureAdminMetaPatch> = {};
    for (const row of rows as Record<string, unknown>[]) {
      const id = row.feature_id as string;
      out[id] = rowToFeatureAdminMetaPatch(row);
    }
    return out;
  } catch {
    return {};
  }
}

export async function dbSmSetFeatureAdminMeta(
  featureId: string,
  patch: SmFeatureAdminMetaPatch,
  updatedBy?: string,
): Promise<Record<string, SmFeatureAdminMetaPatch>> {
  const db = sql();
  const rows = await db`
    SELECT feature_id, operational_domain, feature_type, maturity_level,
           recommended_plan_floor, upgrade_driver, requires_setup, requires_provider,
           hidden_from_marketing, requires, aliases, tags, notes
    FROM sm_feature_admin_meta
    WHERE feature_id = ${featureId}
    LIMIT 1
  `;
  const existing = rows[0] as Record<string, unknown> | undefined;
  const merged = {
    ...rowToFeatureAdminMetaPatch(existing ?? {}),
    ...patch,
  };
  const requires = JSON.stringify(merged.requires ?? []);
  const aliases = JSON.stringify(merged.aliases ?? []);
  const tags = JSON.stringify(merged.tags ?? []);
  await db`
    INSERT INTO sm_feature_admin_meta (
      feature_id, operational_domain, feature_type, maturity_level,
      recommended_plan_floor, upgrade_driver, requires_setup, requires_provider,
      hidden_from_marketing, requires, aliases, tags, notes, updated_at, updated_by
    ) VALUES (
      ${featureId},
      ${merged.operationalDomain ?? null},
      ${merged.featureType ?? null},
      ${merged.maturityLevel ?? null},
      ${merged.recommendedPlanFloor ?? null},
      ${merged.upgradeDriver ?? null},
      ${merged.requiresSetup ?? null},
      ${merged.requiresProvider ?? null},
      ${merged.hiddenFromMarketing ?? null},
      ${requires}::jsonb,
      ${aliases}::jsonb,
      ${tags}::jsonb,
      ${merged.notes ?? null},
      NOW(),
      ${updatedBy ?? null}
    )
    ON CONFLICT (feature_id) DO UPDATE SET
      operational_domain = EXCLUDED.operational_domain,
      feature_type = EXCLUDED.feature_type,
      maturity_level = EXCLUDED.maturity_level,
      recommended_plan_floor = EXCLUDED.recommended_plan_floor,
      upgrade_driver = EXCLUDED.upgrade_driver,
      requires_setup = EXCLUDED.requires_setup,
      requires_provider = EXCLUDED.requires_provider,
      hidden_from_marketing = EXCLUDED.hidden_from_marketing,
      requires = EXCLUDED.requires,
      aliases = EXCLUDED.aliases,
      tags = EXCLUDED.tags,
      notes = EXCLUDED.notes,
      updated_at = NOW(),
      updated_by = EXCLUDED.updated_by
  `;
  return dbSmListFeatureAdminMeta();
}

// ─── PLANS (catálogo de planes) ───────────────────────────────────────────────

export interface SmPlanRecord {
  id: string;
  name: string;
  tagline: string;
  priceUsd: number | null;
  priceArs: number | null;
  annualDiscount: boolean;
  highlighted: boolean;
  limits: {
    products: number | null;
    ordersPerMonth: number | null;
    locations: number | null;
    categories: number | null;
    brands: number | null;
    b2bUsers: number | null;
    teamMembers: number | null;
    images: number | null;
    emailsPerDay: number | null;
    pushPerDay: number | null;
    calendarSync: number | null;
    storefront: boolean;
    portalB2b: boolean;
    renataCreditsMonth: number | null;
    renataCreditsExpiryDays: number | null;
    /** IA conectada (MCP): créditos por mes (0 = sin MCP, null = ilimitado) y usuarios conectables. */
    mcpCreditsMonth?: number | null;
    mcpMaxUsers?: number | null;
  };
  featureIds: string[];
  analyticsMetricDefaults: Record<string, boolean>;
  emailTemplateGateDefaults: Record<string, boolean>;
  isSystem: boolean;
  isPublic: boolean;
  sortOrder: number;
}

/** The MCP migration seeded snake_case keys; the plan matrix edits camelCase. Surface the seeded value until saved. */
function withMcpLimitAliases(limits: SmPlanRecord["limits"] | null): SmPlanRecord["limits"] | null {
  if (!limits) return limits;
  const raw = limits as unknown as Record<string, unknown>;
  const out = { ...limits };
  if (out.mcpCreditsMonth === undefined && raw.mcp_credits_month !== undefined) out.mcpCreditsMonth = raw.mcp_credits_month == null ? null : Number(raw.mcp_credits_month);
  if (out.mcpMaxUsers === undefined && raw.mcp_max_users !== undefined) out.mcpMaxUsers = raw.mcp_max_users == null ? null : Number(raw.mcp_max_users);
  return out;
}

export function rowToSmPlan(r: Record<string, unknown>): SmPlanRecord {
  return {
    id: String(r.id),
    name: String(r.name),
    tagline: String(r.tagline ?? ""),
    priceUsd: r.price_usd != null ? Number(r.price_usd) : null,
    priceArs: r.price_ars != null ? Number(r.price_ars) : null,
    annualDiscount: !!r.annual_discount,
    highlighted: !!r.highlighted,
    limits: withMcpLimitAliases(r.limits as SmPlanRecord["limits"] | null) ?? ({
      products: 50,
      ordersPerMonth: 100,
      locations: 1,
      categories: 40,
      brands: 25,
      b2bUsers: null,
      images: null,
      emailsPerDay: 10,
      pushPerDay: 50,
      calendarSync: null,
      storefront: true,
      portalB2b: false,
    } as SmPlanRecord["limits"]),
    featureIds: Array.isArray(r.feature_ids) ? (r.feature_ids as string[]) : [],
    analyticsMetricDefaults:
      r.analytics_metric_defaults && typeof r.analytics_metric_defaults === "object"
        ? (r.analytics_metric_defaults as Record<string, boolean>)
        : {},
    emailTemplateGateDefaults:
      r.email_template_gate_defaults && typeof r.email_template_gate_defaults === "object"
        ? (r.email_template_gate_defaults as Record<string, boolean>)
        : {},
    isSystem: !!r.is_system,
    isPublic: !!r.is_public,
    sortOrder: Number(r.sort_order ?? 0),
  };
}

const SM_SYSTEM_PLAN_IDS = ["starter", "pro", "business", "enterprise", "free_trial"];

/** Lista planes desde DB. Devuelve [] si la tabla no existe (fallback estático en el caller). */
export async function dbSmListPlans(): Promise<SmPlanRecord[]> {
  try {
    const db = sql();
    const rows = await db`SELECT * FROM sm_plans ORDER BY sort_order, name`;
    return rows.map((r) => rowToSmPlan(r as Record<string, unknown>));
  } catch {
    return [];
  }
}

export async function dbSmGetPlan(id: string): Promise<SmPlanRecord | null> {
  try {
    const db = sql();
    const rows = await db`SELECT * FROM sm_plans WHERE id = ${id} LIMIT 1`;
    return rows[0] ? rowToSmPlan(rows[0] as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export async function dbSmCreatePlan(p: Omit<SmPlanRecord, "isSystem">): Promise<SmPlanRecord> {
  const db = sql();
  const rows = await db`
    INSERT INTO sm_plans (id, name, tagline, price_usd, price_ars, annual_discount, highlighted, limits, feature_ids, analytics_metric_defaults, email_template_gate_defaults, is_system, is_public, sort_order, updated_at)
    VALUES (${p.id}, ${p.name}, ${p.tagline}, ${p.priceUsd}, ${p.priceArs}, ${p.annualDiscount}, ${p.highlighted},
            ${JSON.stringify(p.limits)}, ${JSON.stringify(p.featureIds)},
            ${JSON.stringify(p.analyticsMetricDefaults ?? {})}::jsonb,
            ${JSON.stringify(p.emailTemplateGateDefaults ?? {})}::jsonb,
            FALSE, ${p.isPublic}, ${p.sortOrder}, NOW())
    RETURNING *`;
  return rowToSmPlan(rows[0] as Record<string, unknown>);
}

export async function dbSmUpdatePlan(id: string, p: Partial<Omit<SmPlanRecord, "id" | "isSystem">>): Promise<SmPlanRecord | null> {
  const db = sql();
  const cur = await dbSmGetPlan(id);
  if (!cur) return null;
  const m = { ...cur, ...p };
  const rows = await db`
    UPDATE sm_plans SET
      name = ${m.name}, tagline = ${m.tagline}, price_usd = ${m.priceUsd}, price_ars = ${m.priceArs},
      annual_discount = ${m.annualDiscount}, highlighted = ${m.highlighted},
      limits = ${JSON.stringify(m.limits)}, feature_ids = ${JSON.stringify(m.featureIds)},
      analytics_metric_defaults = ${JSON.stringify(m.analyticsMetricDefaults ?? {})}::jsonb,
      email_template_gate_defaults = ${JSON.stringify(m.emailTemplateGateDefaults ?? {})}::jsonb,
      is_public = ${m.isPublic}, sort_order = ${m.sortOrder}, updated_at = NOW()
    WHERE id = ${id} RETURNING *`;
  return rows[0] ? rowToSmPlan(rows[0] as Record<string, unknown>) : null;
}

export async function dbSmDeletePlan(id: string): Promise<{ ok: boolean; reason?: string }> {
  if (SM_SYSTEM_PLAN_IDS.includes(id)) return { ok: false, reason: "No se puede eliminar un plan del sistema" };
  const db = sql();
  const cur = await dbSmGetPlan(id);
  if (cur?.isSystem) return { ok: false, reason: "No se puede eliminar un plan del sistema" };
  await db`DELETE FROM sm_plans WHERE id = ${id}`;
  return { ok: true };
}

// ─── AUDIT LOG ────────────────────────────────────────────────────────────────

export async function dbSmAppendAuditLog(input: {
  tenantId: string; action: string; entityTable: string;
  entityId?: string | null; actorUserId?: string | null;
  before?: unknown; after?: unknown; meta?: Record<string, unknown>;
}): Promise<void> {
  const db = sql();
  const id = nextId("alog");
  await db`
    INSERT INTO sm_audit_logs (id, tenant_id, action, entity_table, entity_id, actor_user_id, before_data, after_data, meta)
    VALUES (
      ${id}, ${input.tenantId}, ${input.action}, ${input.entityTable},
      ${input.entityId ?? null}, ${input.actorUserId ?? null},
      ${input.before ? JSON.stringify(input.before) : null},
      ${input.after  ? JSON.stringify(input.after)  : null},
      ${JSON.stringify(input.meta ?? {})}
    )
    ON CONFLICT (id) DO NOTHING
  `;
}

export type AuditLogAdminFilters = {
  from?: string | null;
  to?: string | null;
  tenantId?: string | null;
  actorQuery?: string | null;
  action?: string | null;
  entityTable?: string | null;
  q?: string | null;
  limit?: number;
  offset?: number;
};

function auditLogWhereFragment(db: ReturnType<typeof sql>, f: AuditLogAdminFilters) {
  const qLike = f.q ? `%${f.q.toLowerCase()}%` : null;
  const actorLike = f.actorQuery ? `%${f.actorQuery.toLowerCase()}%` : null;
  return db`
    WHERE 1=1
    ${f.from ? db`AND a.created_at >= ${f.from}` : db``}
    ${f.to ? db`AND a.created_at <= ${f.to}` : db``}
    ${f.tenantId ? db`AND a.tenant_id = ${f.tenantId}` : db``}
    ${f.action ? db`AND a.action = ${f.action}` : db``}
    ${f.entityTable ? db`AND a.entity_table = ${f.entityTable}` : db``}
    ${actorLike ? db`AND (lower(u.name) LIKE ${actorLike} OR lower(u.email) LIKE ${actorLike})` : db``}
    ${qLike ? db`AND (
      lower(a.action) LIKE ${qLike}
      OR lower(a.entity_table) LIKE ${qLike}
      OR lower(COALESCE(a.entity_id, '')) LIKE ${qLike}
      OR lower(COALESCE(t.name, '')) LIKE ${qLike}
      OR lower(COALESCE(u.name, '')) LIKE ${qLike}
    )` : db``}
  `;
}

/** Auditoría — lista filtrada/paginada (SCRUM-319). Incluye before/after reales cuando existen. */
export async function dbSmListAuditLogsAdmin(filters: AuditLogAdminFilters = {}) {
  const db = sql();
  const limit = Math.min(Math.max(filters.limit ?? 100, 1), 200);
  const offset = Math.max(filters.offset ?? 0, 0);
  const whereFragment = auditLogWhereFragment(db, filters);
  const rows = await db`
    SELECT a.*, t.name AS tenant_name, t.slug AS tenant_slug,
           u.name AS actor_name, u.email AS actor_email
    FROM sm_audit_logs a
    LEFT JOIN sm_tenants t ON t.id = a.tenant_id
    LEFT JOIN sm_users u ON u.id = a.actor_user_id
    ${whereFragment}
    ORDER BY a.created_at DESC
    LIMIT ${limit} OFFSET ${offset}
  `;
  return rows.map((r) => ({
    id: String(r.id),
    tenantId: String(r.tenant_id),
    tenantName: (r.tenant_name as string | null) ?? null,
    tenantSlug: (r.tenant_slug as string | null) ?? null,
    action: String(r.action),
    entityTable: String(r.entity_table),
    entityId: r.entity_id != null ? String(r.entity_id) : null,
    actorUserId: r.actor_user_id != null ? String(r.actor_user_id) : null,
    actorName: (r.actor_name as string | null) ?? null,
    actorEmail: (r.actor_email as string | null) ?? null,
    createdAt: rowValueToIsoDateTime(r.created_at),
    before: (r.before_data as unknown) ?? null,
    after: (r.after_data as unknown) ?? null,
    meta: (r.meta as Record<string, unknown>) ?? {},
  }));
}

/** KPIs de auditoría sobre el mismo universo filtrado (SCRUM-319) — agregado en SQL, no en cliente. */
export async function dbSmGetAuditLogKpis(filters: AuditLogAdminFilters = {}) {
  const db = sql();
  const whereFragment = auditLogWhereFragment(db, filters);
  const [row] = await db`
    SELECT
      COUNT(*)::int AS total,
      COUNT(*) FILTER (WHERE a.created_at >= CURRENT_DATE)::int AS today,
      COUNT(DISTINCT a.actor_user_id)::int AS distinct_actors,
      COUNT(DISTINCT a.tenant_id)::int AS distinct_tenants,
      COUNT(*) FILTER (WHERE a.before_data IS NOT NULL OR a.after_data IS NOT NULL)::int AS with_diff
    FROM sm_audit_logs a
    LEFT JOIN sm_tenants t ON t.id = a.tenant_id
    LEFT JOIN sm_users u ON u.id = a.actor_user_id
    ${whereFragment}
  `;
  return {
    total: Number(row?.total ?? 0),
    today: Number(row?.today ?? 0),
    distinctActors: Number(row?.distinct_actors ?? 0),
    distinctTenants: Number(row?.distinct_tenants ?? 0),
    withDiff: Number(row?.with_diff ?? 0),
  };
}

/** Valores distintos reales para poblar los selects de filtro (SCRUM-319) — no una lista hardcodeada. */
export async function dbSmListAuditLogDistinctValues(): Promise<{ actions: string[]; entityTables: string[] }> {
  const db = sql();
  const [actionRows, tableRows] = await Promise.all([
    db`SELECT DISTINCT action FROM sm_audit_logs ORDER BY action ASC LIMIT 500`,
    db`SELECT DISTINCT entity_table FROM sm_audit_logs ORDER BY entity_table ASC LIMIT 200`,
  ]);
  return {
    actions: actionRows.map((r) => String(r.action)),
    entityTables: tableRows.map((r) => String(r.entity_table)),
  };
}

// ─── PARTNERS & REFERRALS ─────────────────────────────────────────────────────

function genReferralCode(): string {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  return Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map((b) => chars[b % chars.length])
    .join("");
}

function partnerSiteBase(): string {
  return getPublicSiteUrl();
}

function rowToPartner(r: Record<string, unknown>): Partner {
  const code = String(r.referral_code);
  return {
    id: String(r.id),
    name: String(r.name),
    email: String(r.email),
    company: (r.company as string | null) ?? null,
    referralCode: code,
    referralUrl: String(r.referral_url ?? `${partnerSiteBase()}/register?ref=${code}`),
    commissionType: (r.commission_type as Partner["commissionType"]) ?? "cash",
    commissionPct: Number(r.commission_pct) || 5,
    creditBalance: Number(r.credit_balance) || 0,
    totalReferred: Number(r.total_referred) || 0,
    totalEarned: Number(r.total_earned) || 0,
    status: (r.status as Partner["status"]) ?? "active",
    notes: (r.notes as string | null) ?? null,
    createdAt: rowValueToIsoDateTime(r.created_at),
    updatedAt: rowValueToIsoDateTime(r.updated_at),
  };
}

function rowToReferral(r: Record<string, unknown>): Referral {
  return {
    id: String(r.id),
    partnerId: String(r.partner_id),
    tenantId: (r.tenant_id as string | null) ?? null,
    referralCode: String(r.referral_code),
    planId: (r.plan_id as string | null) ?? null,
    planPeriod: (r.plan_period as string | null) ?? null,
    originalAmount: r.original_amount != null ? Number(r.original_amount) : null,
    discountedAmount: r.discounted_amount != null ? Number(r.discounted_amount) : null,
    discountPct: Number(r.discount_pct) || 10,
    commissionAmount: r.commission_amount != null ? Number(r.commission_amount) : null,
    commissionStatus: (r.commission_status as Referral["commissionStatus"]) ?? "pending",
    paidAt: r.paid_at ? String(r.paid_at).slice(0, 10) : null,
    createdAt: rowValueToIsoDateTime(r.created_at),
  };
}

export async function dbSmListPartners(): Promise<Partner[]> {
  const db = sql();
  const rows = await db`SELECT * FROM sm_partners ORDER BY created_at DESC`;
  return rows.map((r) => rowToPartner(r as Record<string, unknown>));
}

export async function dbSmGetPartnerByCode(code: string): Promise<Partner | null> {
  const db = sql();
  const rows = await db`
    SELECT * FROM sm_partners WHERE referral_code = ${code.toUpperCase()} AND status = 'active' LIMIT 1
  `;
  return rows[0] ? rowToPartner(rows[0] as Record<string, unknown>) : null;
}

export async function dbSmCreatePartner(input: {
  name: string;
  email: string;
  company?: string;
  commissionType?: "cash" | "credit";
  commissionPct?: number;
  notes?: string;
}): Promise<Partner> {
  const db = sql();
  const id = nextId("prt");
  let code = genReferralCode();
  let exists = await db`SELECT 1 FROM sm_partners WHERE referral_code = ${code} LIMIT 1`;
  while (exists.length > 0) {
    code = genReferralCode();
    exists = await db`SELECT 1 FROM sm_partners WHERE referral_code = ${code} LIMIT 1`;
  }
  const referralUrl = `${partnerSiteBase()}/register?ref=${code}`;
  await db`
    INSERT INTO sm_partners (id, name, email, company, referral_code, referral_url, commission_type, commission_pct, notes)
    VALUES (
      ${id}, ${input.name}, ${input.email}, ${input.company ?? null}, ${code}, ${referralUrl},
      ${input.commissionType ?? "cash"}, ${input.commissionPct ?? 5}, ${input.notes ?? null}
    )
  `;
  const rows = await db`SELECT * FROM sm_partners WHERE id = ${id}`;
  return rowToPartner(rows[0] as Record<string, unknown>);
}

export async function dbSmUpdatePartner(
  id: string,
  patch: Partial<{
    name: string;
    email: string;
    company: string;
    commissionType: "cash" | "credit";
    commissionPct: number;
    status: "active" | "paused" | "inactive";
    notes: string;
    creditBalance: number;
  }>,
): Promise<void> {
  const db = sql();
  const cur = await db`SELECT * FROM sm_partners WHERE id = ${id} LIMIT 1`;
  if (!cur[0]) throw new Error("Partner no encontrado");
  const r = cur[0] as Record<string, unknown>;
  await db`
    UPDATE sm_partners SET
      name = ${patch.name ?? r.name},
      email = ${patch.email ?? r.email},
      company = ${patch.company !== undefined ? patch.company : r.company},
      commission_type = ${patch.commissionType ?? r.commission_type},
      commission_pct = ${patch.commissionPct ?? r.commission_pct},
      status = ${patch.status ?? r.status},
      notes = ${patch.notes !== undefined ? patch.notes : r.notes},
      credit_balance = ${patch.creditBalance ?? r.credit_balance},
      updated_at = NOW()
    WHERE id = ${id}
  `;
}

export async function dbSmListReferrals(partnerId?: string): Promise<Referral[]> {
  const db = sql();
  const rows = partnerId
    ? await db`SELECT * FROM sm_referrals WHERE partner_id = ${partnerId} ORDER BY created_at DESC`
    : await db`SELECT * FROM sm_referrals ORDER BY created_at DESC`;
  return rows.map((r) => rowToReferral(r as Record<string, unknown>));
}

export async function dbSmCreateReferral(input: {
  partnerId: string;
  tenantId?: string;
  referralCode: string;
  planId?: string;
  planPeriod?: string;
  originalAmount?: number;
  discountedAmount?: number;
  discountPct: number;
  commissionAmount?: number;
}): Promise<Referral> {
  const db = sql();
  const id = nextId("ref");
  await db`
    INSERT INTO sm_referrals (
      id, partner_id, tenant_id, referral_code, plan_id, plan_period,
      original_amount, discounted_amount, discount_pct, commission_amount
    )
    VALUES (
      ${id}, ${input.partnerId}, ${input.tenantId ?? null}, ${input.referralCode},
      ${input.planId ?? null}, ${input.planPeriod ?? null},
      ${input.originalAmount ?? null}, ${input.discountedAmount ?? null},
      ${input.discountPct}, ${input.commissionAmount ?? null}
    )
  `;
  await db`UPDATE sm_partners SET total_referred = total_referred + 1, updated_at = NOW() WHERE id = ${input.partnerId}`;
  const rows = await db`SELECT * FROM sm_referrals WHERE id = ${id}`;
  return rowToReferral(rows[0] as Record<string, unknown>);
}

export async function dbSmUpdateReferralStatus(
  id: string,
  status: "approved" | "paid" | "cancelled",
  paidAt?: string,
): Promise<void> {
  const db = sql();
  if (status === "paid" && paidAt) {
    await db`UPDATE sm_referrals SET commission_status = ${status}, paid_at = ${paidAt} WHERE id = ${id}`;
  } else {
    await db`UPDATE sm_referrals SET commission_status = ${status} WHERE id = ${id}`;
  }
  if (status === "paid") {
    const rows = await db`SELECT commission_amount, partner_id FROM sm_referrals WHERE id = ${id}`;
    if (rows[0]?.commission_amount) {
      await db`
        UPDATE sm_partners SET total_earned = total_earned + ${rows[0].commission_amount}, updated_at = NOW()
        WHERE id = ${rows[0].partner_id}
      `;
    }
  }
}

// ─── CONFIG SNAPSHOTS (ops) — SCRUM-320: versionado de configuración ──────────

function mapConfigSnapshotRow(r: Record<string, unknown>) {
  return {
    id: String(r.id),
    tenantId: String(r.tenant_id),
    name: String(r.name),
    createdByUserId: r.created_by_user_id != null ? String(r.created_by_user_id) : null,
    createdAt: rowValueToIsoDateTime(r.created_at),
    payload: (r.payload as Record<string, unknown>) ?? {},
    origin: (r.origin as "auto" | "manual" | "restore" | undefined) ?? "manual",
    domains: (r.domains as string[]) ?? [],
    fingerprint: r.fingerprint != null ? String(r.fingerprint) : null,
    restoredFromSnapshotId: r.restored_from_snapshot_id != null ? String(r.restored_from_snapshot_id) : null,
  };
}

export async function dbSmListConfigSnapshots(tenantId: string, limit = 100) {
  const db = sql();
  const rows = await db`
    SELECT s.*, u.name AS actor_name FROM sm_config_snapshots s
    LEFT JOIN sm_users u ON u.id = s.created_by_user_id
    WHERE s.tenant_id = ${tenantId}
    ORDER BY s.created_at DESC LIMIT ${limit}
  `;
  return rows.map((r) => ({ ...mapConfigSnapshotRow(r as Record<string, unknown>), actorName: r.actor_name != null ? String(r.actor_name) : null }));
}

export async function dbSmCreateConfigSnapshot(input: {
  tenantId: string;
  name: string;
  createdByUserId?: string | null;
  payload: Record<string, unknown>;
  origin?: "auto" | "manual" | "restore";
  domains?: string[];
  fingerprint?: string | null;
  restoredFromSnapshotId?: string | null;
}) {
  const db = sql();
  const id = nextId("snap");
  // Defensa en profundidad (SCRUM-320): sea cual sea el caller, si payload.config
  // trae la fila cruda de sm_tenant_config, nunca persistir secrets/tokens —
  // enmascarar siempre antes del INSERT, sin importar de dónde vino el payload.
  if (input.payload && typeof input.payload === "object" && "config" in input.payload) {
    const { buildVersionableConfigSnapshot } = await import("@/lib/tenant-config-versioning");
    input = { ...input, payload: { ...input.payload, config: buildVersionableConfigSnapshot(input.payload.config as Record<string, unknown>) } };
  }
  await db`
    INSERT INTO sm_config_snapshots (id, tenant_id, name, created_by_user_id, payload, origin, domains, fingerprint, restored_from_snapshot_id)
    VALUES (
      ${id}, ${input.tenantId}, ${input.name}, ${input.createdByUserId ?? null}, ${JSON.stringify(input.payload)},
      ${input.origin ?? "manual"}, ${JSON.stringify(input.domains ?? [])}::jsonb, ${input.fingerprint ?? null}, ${input.restoredFromSnapshotId ?? null}
    )
  `;
  return id;
}

export async function dbSmGetConfigSnapshot(snapshotId: string) {
  const db = sql();
  const rows = await db`
    SELECT s.*, u.name AS actor_name FROM sm_config_snapshots s
    LEFT JOIN sm_users u ON u.id = s.created_by_user_id
    WHERE s.id = ${snapshotId} LIMIT 1
  `;
  if (!rows.length) return null;
  const r = rows[0];
  return { ...mapConfigSnapshotRow(r as Record<string, unknown>), actorName: r.actor_name != null ? String(r.actor_name) : null };
}

/**
 * Crea una versión automática de la configuración actual del tenant, con
 * secrets excluidos/enmascarados (SCRUM-320). No crea nada si el patch no
 * tocó columnas versionables, o si el fingerprint es idéntico al último
 * (no-op real). Fire-and-forget desde dbSmPatchTenantConfig — nunca lanza.
 */
/** Fila cruda de sm_tenant_config (snake_case) — para versionado, nunca para exponer al frontend sin enmascarar. */
export async function dbSmGetRawTenantConfigRow(tenantId: string): Promise<Record<string, unknown> | null> {
  const [row] = await sql()`SELECT * FROM sm_tenant_config WHERE tenant_id = ${tenantId}`;
  return (row as Record<string, unknown>) ?? null;
}

export async function dbSmCreateAutoConfigVersion(opts: { tenantId: string; changedColumns: string[]; actorUserId?: string | null }): Promise<void> {
  try {
    const { buildVersionableConfigSnapshot, domainsForColumns } = await import("@/lib/tenant-config-versioning");
    const domains = domainsForColumns(opts.changedColumns);
    if (domains.length === 0) return;

    const db = sql();
    const [configRow] = await db`SELECT * FROM sm_tenant_config WHERE tenant_id = ${opts.tenantId}`;
    if (!configRow) return;

    const snapshot = buildVersionableConfigSnapshot(configRow as Record<string, unknown>);
    const crypto = await import("node:crypto");
    const fp = crypto.createHash("sha256").update(JSON.stringify(snapshot)).digest("hex").slice(0, 16);

    const [last] = await db`
      SELECT fingerprint FROM sm_config_snapshots WHERE tenant_id = ${opts.tenantId} ORDER BY created_at DESC LIMIT 1
    `;
    if (last && String(last.fingerprint) === fp) return;

    const id = await dbSmCreateConfigSnapshot({
      tenantId: opts.tenantId,
      name: `Auto — ${domains.join(", ")}`,
      createdByUserId: opts.actorUserId ?? null,
      payload: { config: snapshot },
      origin: "auto",
      domains,
      fingerprint: fp,
    });

    const { logAuditEntry } = await import("@/lib/ops-logger");
    logAuditEntry({
      tenantId: opts.tenantId,
      action: "tenant_config.version.auto",
      entityTable: "sm_config_snapshots",
      entityId: id,
      actorUserId: opts.actorUserId ?? null,
      meta: { domains },
    });
  } catch {
    // best-effort: nunca debe romper el patch de configuración real
  }
}

/**
 * Restaura columnas de un snapshot pasado, filtradas por dominio, vía el
 * escritor canónico dbSmPatchTenantConfig — nunca un UPDATE directo. Nunca
 * restaura columnas enmascaradas (no son el valor real). Nunca reescribe
 * historia: crea un nuevo snapshot con origin="restore".
 */
export async function dbSmRestoreConfigSnapshotDomains(opts: {
  tenantId: string;
  snapshotId: string;
  domains: string[];
  actorUserId?: string | null;
}): Promise<{ id: string; columnsRestored: string[] } | { error: string }> {
  const { TENANT_CONFIG_DOMAIN_COLUMNS, MASKED_CONFIG_COLUMNS, buildVersionableConfigSnapshot } = await import("@/lib/tenant-config-versioning");

  const snap = await dbSmGetConfigSnapshot(opts.snapshotId);
  if (!snap || snap.tenantId !== opts.tenantId) return { error: "Snapshot no encontrado" };

  const configSnapshot = (snap.payload as { config?: Record<string, unknown> }).config ?? {};
  const columns = opts.domains.flatMap((d) => TENANT_CONFIG_DOMAIN_COLUMNS[d as keyof typeof TENANT_CONFIG_DOMAIN_COLUMNS] ?? []);
  const patch: Record<string, unknown> = {};
  for (const col of columns) {
    if (col in configSnapshot && !MASKED_CONFIG_COLUMNS.has(col)) patch[col] = configSnapshot[col];
  }
  if (Object.keys(patch).length === 0) return { error: "Ninguna columna aplicable en los dominios seleccionados" };

  await dbSmPatchTenantConfig(opts.tenantId, patch, opts.actorUserId ?? null, true);

  const [configRow] = await sql()`SELECT * FROM sm_tenant_config WHERE tenant_id = ${opts.tenantId}`;
  const newSnapshot = buildVersionableConfigSnapshot((configRow ?? {}) as Record<string, unknown>);
  const crypto = await import("node:crypto");
  const fp = crypto.createHash("sha256").update(JSON.stringify(newSnapshot)).digest("hex").slice(0, 16);

  const id = await dbSmCreateConfigSnapshot({
    tenantId: opts.tenantId,
    name: `Restaurado desde ${opts.snapshotId}`,
    createdByUserId: opts.actorUserId ?? null,
    payload: { config: newSnapshot },
    origin: "restore",
    domains: opts.domains,
    fingerprint: fp,
    restoredFromSnapshotId: opts.snapshotId,
  });

  const { logAuditEntry } = await import("@/lib/ops-logger");
  logAuditEntry({
    tenantId: opts.tenantId,
    action: "tenant_config.version.restore",
    entityTable: "sm_config_snapshots",
    entityId: id,
    actorUserId: opts.actorUserId ?? null,
    meta: { domains: opts.domains, restoredFromSnapshotId: opts.snapshotId, columns: Object.keys(patch) },
  });

  return { id, columnsRestored: Object.keys(patch) };
}

export async function dbSmApplyConfigSnapshot(
  tenantId: string,
  snapshotId: string,
  mode: "merge" | "replace",
  dryRun: boolean,
): Promise<{ applied: boolean; diff: Record<string, unknown> }> {
  const snap = await dbSmGetConfigSnapshot(snapshotId);
  if (!snap || snap.tenantId !== tenantId) {
    return { applied: false, diff: { error: "Snapshot no encontrado" } };
  }
  const payload = snap.payload as { tenant?: Record<string, unknown>; config?: Record<string, unknown> };
  const tenantPatch = { ...(payload.tenant ?? {}) };
  delete tenantPatch.id;
  delete tenantPatch.slug;
  const configPatch = { ...(payload.config ?? {}) };

  if (dryRun) {
    return {
      applied: false,
      diff: {
        mode,
        tenantFields: Object.keys(tenantPatch),
        configFields: Object.keys(configPatch),
      },
    };
  }

  if (Object.keys(tenantPatch).length) await dbSmPatchTenant(tenantId, tenantPatch);
  if (Object.keys(configPatch).length) await dbSmPatchTenantConfig(tenantId, configPatch);

  return { applied: true, diff: { mode, snapshotId, tenantFields: Object.keys(tenantPatch), configFields: Object.keys(configPatch) } };
}
// ─── CRON RUNS ───────────────────────────────────────────────────────────────

export async function dbSmRecordCronSuccess(opts: {
  id: string;
  jobId: string;
  requestId?: string;
  message?: string;
  payload?: Record<string, unknown>;
  durationMs?: number;
}): Promise<void> {
  try {
    const db = sql();
    await db`
      INSERT INTO sm_cron_runs (id, job_id, status, request_id, message, payload, duration_ms)
      VALUES (${opts.id}, ${opts.jobId}, 'success', ${opts.requestId ?? null},
              ${opts.message ?? null}, ${JSON.stringify(opts.payload ?? {})}::jsonb,
              ${opts.durationMs ?? null})
    `;
  } catch {
    // No lanzar — el cron ya completó; fallar aquí no debe bloquear la respuesta
  }
}

export async function dbSmRecordCronError(opts: {
  id: string;
  jobId: string;
  requestId?: string;
  error: string;
  durationMs?: number;
}): Promise<void> {
  try {
    const db = sql();
    await db`
      INSERT INTO sm_cron_runs (id, job_id, status, request_id, message, duration_ms)
      VALUES (${opts.id}, ${opts.jobId}, 'error', ${opts.requestId ?? null},
              ${opts.error}, ${opts.durationMs ?? null})
    `;
  } catch {
    // idem
  }
}

export async function dbSmListCronRuns(
  jobId?: string,
  limit = 48,
  offset = 0,
): Promise<{ id: string; jobId: string; status: string; message: string | null; durationMs: number | null; executedAt: string }[]> {
  try {
    const db = sql();
    const rows = jobId
      ? await db`SELECT * FROM sm_cron_runs WHERE job_id = ${jobId} ORDER BY executed_at DESC LIMIT ${limit} OFFSET ${offset}`
      : await db`SELECT * FROM sm_cron_runs ORDER BY executed_at DESC LIMIT ${limit} OFFSET ${offset}`;
    return rows.map((r) => ({
    id: String(r.id),
    jobId: String(r.job_id),
    status: String(r.status),
    message: r.message ? String(r.message) : null,
    durationMs: r.duration_ms ? Number(r.duration_ms) : null,
    executedAt: String(r.executed_at),
    }));
  } catch (err) {
    const msg = String(err);
    if (msg.includes("sm_cron_runs") || msg.includes("does not exist")) return [];
    throw err;
  }
}

export type CronRunsSummaryRow = {
  jobId: string;
  total: number;
  errors: number;
  lastStatus: string | null;
  lastMessage: string | null;
  lastExecutedAt: string | null;
  avgDurationMs: number | null;
};

/** Agregación por job en un período (SCRUM-313) — GROUP BY real, no fetch-all-y-contar en memoria. */
export async function dbSmGetCronRunsSummary(sinceIso: string): Promise<CronRunsSummaryRow[]> {
  try {
    const db = sql();
    const rows = await db`
      SELECT
        job_id,
        COUNT(*)::int AS total,
        COUNT(*) FILTER (WHERE status = 'error')::int AS errors,
        (array_agg(status ORDER BY executed_at DESC))[1] AS last_status,
        (array_agg(message ORDER BY executed_at DESC))[1] AS last_message,
        MAX(executed_at) AS last_executed_at,
        AVG(duration_ms)::int AS avg_duration_ms
      FROM sm_cron_runs
      WHERE executed_at >= ${sinceIso}
      GROUP BY job_id
      ORDER BY errors DESC, total DESC
    `;
    return rows.map((r) => ({
      jobId: String(r.job_id),
      total: Number(r.total),
      errors: Number(r.errors),
      lastStatus: r.last_status != null ? String(r.last_status) : null,
      lastMessage: r.last_message != null ? String(r.last_message) : null,
      lastExecutedAt: r.last_executed_at != null ? String(r.last_executed_at) : null,
      avgDurationMs: r.avg_duration_ms != null ? Number(r.avg_duration_ms) : null,
    }));
  } catch (err) {
    const msg = String(err);
    if (msg.includes("sm_cron_runs") || msg.includes("does not exist")) return [];
    throw err;
  }
}
export async function dbAppendHelpAnalyticsEvent(input: {
  tenantId: string;
  eventType: string;
  articleId?: string | null;
  query?: string | null;
  resultCount?: number | null;
  tourId?: string | null;
  itemId?: string | null;
  meta?: Record<string, unknown>;
}): Promise<void> {
  try {
    const db = sql();
    await db`
      INSERT INTO sm_help_analytics
        (id, tenant_id, event_type, article_id, query, result_count, tour_id, item_id, meta)
      VALUES (
        ${nextId("ha")}, ${input.tenantId}, ${input.eventType},
        ${input.articleId ?? null}, ${input.query ?? null}, ${input.resultCount ?? null},
        ${input.tourId ?? null}, ${input.itemId ?? null},
        ${JSON.stringify(input.meta ?? {})}
      )
    `;
  } catch {
    /* tabla puede no existir aún en dev */
  }
}

/**
 * SCRUM-331 — Help & Adoption Analytics + Renata/IA (separadas, no mezcladas).
 * Demos/seed excluidos con el mismo criterio que Usage & Economics (SCRUM-330).
 * "Tours iniciados" no existe como evento real (solo tour_complete/tour_skip/
 * tour_never_show se emiten desde el cliente, ver product-knowledge/help-analytics.ts)
 * — no se inventa una tasa de finalización sobre un denominador que no existe.
 */
export interface HelpAnalyticsSummary {
  topArticles: { articleId: string; views: number }[];
  topSearches: { query: string; count: number }[];
  noResultSearches: { query: string; count: number }[];
  tourCompletions: { tourId: string; completed: number; skipped: number }[];
  totalEvents: number;
  distinctTenants: number;
  renata: {
    conversations: number;
    distinctTenants: number;
    totalMessages: number;
    errorRate: number | null;
    avgDurationMs: number | null;
    topSkills: { skillId: string; count: number }[];
  };
}

export async function dbGetHelpAnalyticsSummary(days = 30): Promise<HelpAnalyticsSummary> {
  const empty: HelpAnalyticsSummary = {
    topArticles: [],
    topSearches: [],
    noResultSearches: [],
    tourCompletions: [],
    totalEvents: 0,
    distinctTenants: 0,
    renata: { conversations: 0, distinctTenants: 0, totalMessages: 0, errorRate: null, avgDurationMs: null, topSkills: [] },
  };
  try {
    const db = sql();
    const since = new Date(Date.now() - days * 86400000).toISOString();
    const notDemo = db`NOT (
      COALESCE(t.product_flags->>'is_demo_seed', 'false') = 'true'
      OR COALESCE(t.product_flags->>'is_demo_lead', 'false') = 'true'
      OR COALESCE(t.product_flags->>'is_demo', 'false') = 'true'
    )`;

    const [articles, searches, noResultSearches, tourRows, total, distinctT] = await Promise.all([
      db`
        SELECT h.article_id, COUNT(*)::int AS cnt
        FROM sm_help_analytics h JOIN sm_tenants t ON t.id = h.tenant_id
        WHERE h.event_type = 'article_view' AND h.article_id IS NOT NULL AND h.created_at >= ${since} AND ${notDemo}
        GROUP BY h.article_id ORDER BY cnt DESC LIMIT 15
      `,
      db`
        SELECT h.query, COUNT(*)::int AS cnt
        FROM sm_help_analytics h JOIN sm_tenants t ON t.id = h.tenant_id
        WHERE h.event_type = 'search' AND h.query IS NOT NULL AND h.created_at >= ${since} AND ${notDemo}
        GROUP BY h.query ORDER BY cnt DESC LIMIT 15
      `,
      db`
        SELECT h.query, COUNT(*)::int AS cnt
        FROM sm_help_analytics h JOIN sm_tenants t ON t.id = h.tenant_id
        WHERE h.event_type = 'search' AND h.query IS NOT NULL AND h.result_count = 0 AND h.created_at >= ${since} AND ${notDemo}
        GROUP BY h.query ORDER BY cnt DESC LIMIT 15
      `,
      db`
        SELECT h.tour_id,
               COUNT(*) FILTER (WHERE h.event_type = 'tour_complete')::int AS completed,
               COUNT(*) FILTER (WHERE h.event_type = 'tour_skip')::int AS skipped
        FROM sm_help_analytics h JOIN sm_tenants t ON t.id = h.tenant_id
        WHERE h.event_type IN ('tour_complete', 'tour_skip') AND h.tour_id IS NOT NULL AND h.created_at >= ${since} AND ${notDemo}
        GROUP BY h.tour_id ORDER BY completed DESC LIMIT 10
      `,
      db`
        SELECT COUNT(*)::int AS cnt FROM sm_help_analytics h JOIN sm_tenants t ON t.id = h.tenant_id
        WHERE h.created_at >= ${since} AND ${notDemo}
      `,
      db`
        SELECT COUNT(DISTINCT h.tenant_id)::int AS cnt FROM sm_help_analytics h JOIN sm_tenants t ON t.id = h.tenant_id
        WHERE h.created_at >= ${since} AND ${notDemo}
      `,
    ]);

    let renata = empty.renata;
    try {
      const [renataAgg, renataSkills] = await Promise.all([
        db`
          SELECT
            COUNT(DISTINCT r.conversation_id)::int AS conversations,
            COUNT(DISTINCT r.tenant_id)::int AS distinct_tenants,
            COUNT(*)::int AS total_messages,
            COUNT(*) FILTER (WHERE r.error IS NOT NULL)::int AS errors,
            AVG(r.duration_ms) AS avg_duration_ms
          FROM renata_audit r JOIN sm_tenants t ON t.id = r.tenant_id
          WHERE r.created_at >= ${since} AND ${notDemo}
        `,
        db`
          SELECT r.skill_id, COUNT(*)::int AS cnt
          FROM renata_audit r JOIN sm_tenants t ON t.id = r.tenant_id
          WHERE r.skill_id IS NOT NULL AND r.created_at >= ${since} AND ${notDemo}
          GROUP BY r.skill_id ORDER BY cnt DESC LIMIT 10
        `,
      ]);
      const agg = renataAgg[0] ?? {};
      const totalMessages = Number(agg.total_messages ?? 0);
      const errors = Number(agg.errors ?? 0);
      renata = {
        conversations: Number(agg.conversations ?? 0),
        distinctTenants: Number(agg.distinct_tenants ?? 0),
        totalMessages,
        errorRate: totalMessages > 0 ? Math.round((errors / totalMessages) * 1000) / 10 : null,
        avgDurationMs: agg.avg_duration_ms != null ? Math.round(Number(agg.avg_duration_ms)) : null,
        topSkills: renataSkills.map((r) => ({ skillId: String(r.skill_id), count: Number(r.cnt) })),
      };
    } catch {
      // renata_audit puede no existir aún en dev
    }

    return {
      topArticles: articles.map((r) => ({ articleId: String(r.article_id), views: Number(r.cnt) })),
      topSearches: searches.map((r) => ({ query: String(r.query), count: Number(r.cnt) })),
      noResultSearches: noResultSearches.map((r) => ({ query: String(r.query), count: Number(r.cnt) })),
      tourCompletions: tourRows.map((r) => ({
        tourId: String(r.tour_id),
        completed: Number(r.completed),
        skipped: Number(r.skipped),
      })),
      totalEvents: Number(total[0]?.cnt ?? 0),
      distinctTenants: Number(distinctT[0]?.cnt ?? 0),
      renata,
    };
  } catch {
    return empty;
  }
}

// ─── ROLE TEMPLATES ───────────────────────────────────────────────────────────

export type SmRoleTemplate = {
  id: string;
  tenantId: string;
  name: string;
  baseRole: "owner" | "staff";
  jobTitle: string | null;
  operationalRoleId: string | null;
  operationalScope: string | null;
  isSystemPreset: boolean;
  permissions: string[];
  /** SCRUM-477/478/479: nivel (none|read|write|admin) elegido por permiso, cuando el permiso soporta sub-niveles. */
  permissionLevels: Record<string, string>;
  allowedModules: string[];
  hiddenModules: string[];
};

function rowToSmRoleTemplate(r: Record<string, unknown>): SmRoleTemplate {
  const perms = r.permissions;
  const levels = r.permission_levels;
  const allowed = r.allowed_modules;
  const hidden = r.hidden_modules;
  return {
    id: String(r.id),
    tenantId: String(r.tenant_id),
    name: String(r.name),
    baseRole: r.base_role === "owner" ? "owner" : "staff",
    jobTitle: r.job_title != null ? String(r.job_title) : null,
    operationalRoleId: r.operational_role_id != null ? String(r.operational_role_id) : null,
    operationalScope: r.operational_scope != null ? String(r.operational_scope) : null,
    isSystemPreset: r.is_system_preset === true,
    permissions: Array.isArray(perms) ? perms.map(String) : [],
    permissionLevels:
      levels && typeof levels === "object" && !Array.isArray(levels)
        ? Object.fromEntries(Object.entries(levels as Record<string, unknown>).map(([k, v]) => [k, String(v)]))
        : {},
    allowedModules: Array.isArray(allowed) ? allowed.map(String) : [],
    hiddenModules: Array.isArray(hidden) ? hidden.map(String) : [],
  };
}

export async function dbSmListRoleTemplates(tenantId: string): Promise<SmRoleTemplate[]> {
  const db = sql();
  try {
    const rows = await db`
      SELECT id, tenant_id, name, base_role, job_title,
        operational_role_id, operational_scope, is_system_preset,
        permissions, permission_levels, allowed_modules, hidden_modules
      FROM sm_workspace_role_templates
      WHERE tenant_id = ${tenantId}
      ORDER BY is_system_preset DESC, name ASC
    `;
    return rows.map((r) => rowToSmRoleTemplate(r));
  } catch {
    try {
      const rows = await db`
        SELECT id, tenant_id, name, base_role, job_title,
          operational_role_id, operational_scope, is_system_preset,
          permissions, allowed_modules, hidden_modules
        FROM sm_workspace_role_templates
        WHERE tenant_id = ${tenantId}
        ORDER BY is_system_preset DESC, name ASC
      `;
      return rows.map((r) => rowToSmRoleTemplate({ ...r, permission_levels: {} }));
    } catch {
      try {
        const rows = await db`
          SELECT id, tenant_id, name, base_role, job_title
          FROM sm_workspace_role_templates
          WHERE tenant_id = ${tenantId}
          ORDER BY name ASC
        `;
        return rows.map((r) => ({
          ...rowToSmRoleTemplate({
            ...r,
            is_system_preset: false,
            permissions: [],
            permission_levels: {},
            allowed_modules: [],
            hidden_modules: [],
          }),
          operationalRoleId: r.job_title != null ? String(r.job_title) : null,
        }));
      } catch {
        return [];
      }
    }
  }
}

/** Lookup directo para el gate de permisos (assertSmStaffPermission) — evita traer todas las plantillas por request. */
export async function dbSmGetRoleTemplateByOperationalRoleId(
  tenantId: string,
  operationalRoleId: string,
): Promise<SmRoleTemplate | null> {
  const db = sql();
  try {
    const rows = await db`
      SELECT id, tenant_id, name, base_role, job_title,
        operational_role_id, operational_scope, is_system_preset,
        permissions, permission_levels, allowed_modules, hidden_modules
      FROM sm_workspace_role_templates
      WHERE tenant_id = ${tenantId} AND operational_role_id = ${operationalRoleId}
      LIMIT 1
    `;
    return rows[0] ? rowToSmRoleTemplate(rows[0]) : null;
  } catch {
    try {
      const rows = await db`
        SELECT id, tenant_id, name, base_role, job_title,
          operational_role_id, operational_scope, is_system_preset,
          permissions, allowed_modules, hidden_modules
        FROM sm_workspace_role_templates
        WHERE tenant_id = ${tenantId} AND operational_role_id = ${operationalRoleId}
        LIMIT 1
      `;
      return rows[0] ? rowToSmRoleTemplate({ ...rows[0], permission_levels: {} }) : null;
    } catch {
      return null;
    }
  }
}

export async function dbSmUpsertRoleTemplate(input: {
  id?: string;
  tenantId: string;
  name: string;
  baseRole: "owner" | "staff";
  jobTitle?: string | null;
  operationalRoleId?: string | null;
  operationalScope?: string | null;
  isSystemPreset?: boolean;
  permissions?: string[];
  /** SCRUM-477/478/479: nivel elegido por permiso (subset de `permissions` con `spec.supportsSubLevels`). */
  permissionLevels?: Record<string, string>;
  allowedModules?: string[];
  hiddenModules?: string[];
}): Promise<SmRoleTemplate> {
  const db = sql();
  const id = input.id?.trim() || nextId("rt");
  const perms = input.permissions ?? [];
  const levels = input.permissionLevels ?? {};
  const allowed = input.allowedModules ?? [];
  const hidden = input.hiddenModules ?? [];
  try {
    await db`
      INSERT INTO sm_workspace_role_templates (
        id, tenant_id, name, base_role, job_title, updated_at,
        operational_role_id, operational_scope, is_system_preset,
        permissions, permission_levels, allowed_modules, hidden_modules
      )
      VALUES (
        ${id}, ${input.tenantId}, ${input.name}, ${input.baseRole}, ${input.jobTitle ?? null}, NOW(),
        ${input.operationalRoleId ?? null}, ${input.operationalScope ?? null},
        ${input.isSystemPreset === true}, ${JSON.stringify(perms)}, ${JSON.stringify(levels)}, ${JSON.stringify(allowed)}, ${JSON.stringify(hidden)}
      )
      ON CONFLICT (id) DO UPDATE SET
        name = EXCLUDED.name,
        base_role = EXCLUDED.base_role,
        job_title = EXCLUDED.job_title,
        operational_role_id = EXCLUDED.operational_role_id,
        operational_scope = EXCLUDED.operational_scope,
        is_system_preset = EXCLUDED.is_system_preset,
        permissions = EXCLUDED.permissions,
        permission_levels = EXCLUDED.permission_levels,
        allowed_modules = EXCLUDED.allowed_modules,
        hidden_modules = EXCLUDED.hidden_modules,
        updated_at = NOW()
    `;
  } catch {
    await db`
      INSERT INTO sm_workspace_role_templates (id, tenant_id, name, base_role, job_title, updated_at)
      VALUES (${id}, ${input.tenantId}, ${input.name}, ${input.baseRole}, ${input.jobTitle ?? input.operationalRoleId ?? null}, NOW())
      ON CONFLICT (id) DO UPDATE SET
        name = EXCLUDED.name,
        base_role = EXCLUDED.base_role,
        job_title = EXCLUDED.job_title,
        updated_at = NOW()
    `;
  }
  let rows: Record<string, unknown>[];
  try {
    rows = await db`
      SELECT id, tenant_id, name, base_role, job_title,
        operational_role_id, operational_scope, is_system_preset,
        permissions, permission_levels, allowed_modules, hidden_modules
      FROM sm_workspace_role_templates WHERE id = ${id} LIMIT 1
    `;
  } catch {
    rows = (
      await db`
        SELECT id, tenant_id, name, base_role, job_title,
          operational_role_id, operational_scope, is_system_preset,
          permissions, allowed_modules, hidden_modules
        FROM sm_workspace_role_templates WHERE id = ${id} LIMIT 1
      `
    ).map((r) => ({ ...r, permission_levels: {} }));
  }
  if (!rows[0]) {
    return {
      id,
      tenantId: input.tenantId,
      name: input.name,
      baseRole: input.baseRole,
      jobTitle: input.jobTitle ?? null,
      operationalRoleId: input.operationalRoleId ?? null,
      operationalScope: input.operationalScope ?? null,
      isSystemPreset: input.isSystemPreset === true,
      permissions: input.permissions ?? [],
      permissionLevels: input.permissionLevels ?? {},
      allowedModules: input.allowedModules ?? [],
      hiddenModules: input.hiddenModules ?? [],
    };
  }
  return rowToSmRoleTemplate(rows[0]);
}

export async function dbSmProvisionOperationalRoleTemplates(
  tenantId: string,
  vertical: "fm" | "bh",
  businessProfile: string,
): Promise<{ profile: string; created: number; updated: number; templates: SmRoleTemplate[] }> {
  const rows = workspaceRoleRowsFromOperationalPresets(vertical, businessProfile, tenantId);
  const existing = await dbSmListRoleTemplates(tenantId);
  const byOpId = new Map(
    existing.filter((t) => t.operationalRoleId).map((t) => [t.operationalRoleId!, t.id]),
  );
  let created = 0;
  let updated = 0;
  const templates: SmRoleTemplate[] = [];
  for (const row of rows) {
    const had = byOpId.has(row.operationalRoleId);
    const tpl = await dbSmUpsertRoleTemplate({
      id: row.id,
      tenantId,
      name: row.name,
      baseRole: row.baseRole,
      jobTitle: row.operationalRoleId,
      operationalRoleId: row.operationalRoleId,
      operationalScope: row.operationalScope,
      isSystemPreset: row.isSystemPreset,
      permissions: row.permissions,
      allowedModules: row.allowedModules,
      hiddenModules: row.hiddenModules,
    });
    templates.push(tpl);
    if (had) updated++;
    else created++;
  }
  return { profile: businessProfile, created, updated, templates };
}

export async function dbSmDeleteRoleTemplate(
  id: string,
  tenantId?: string,
): Promise<boolean> {
  const db = sql();
  try {
    if (tenantId) {
      const rows = await db`
        DELETE FROM sm_workspace_role_templates
        WHERE id = ${id} AND tenant_id = ${tenantId} AND COALESCE(is_system_preset, false) = false
        RETURNING id
      `;
      return rows.length > 0;
    }
    const rows = await db`
      DELETE FROM sm_workspace_role_templates WHERE id = ${id} RETURNING id
    `;
    return rows.length > 0;
  } catch {
    return false;
  }
}

// ─── Niche plan marketing ─────────────────────────────────────────────────────

export interface NichePlanLimitsOverride {
  products?: number | null;
  ordersPerMonth?: number | null;
  locations?: number | null;
  categories?: number | null;
  brands?: number | null;
  b2bUsers?: number | null;
  teamMembers?: number | null;
  images?: number | null;
  emailsPerDay?: number | null;
  pushPerDay?: number | null;
  calendarSync?: number | null;
  storefront?: boolean;
  portalB2b?: boolean;
  renataCreditsMonth?: number | null;
  renataCreditsExpiryDays?: number | null;
}

export interface NicheConfigRow {
  nicheId: string;
  enabled: boolean;
  discount12m: number | null;
  discount24m: number | null;
  discount48m: number | null;
  /** Nombre a mostrar en la landing pública (píldoras de precios). Null = usar nicheId crudo. */
  label: string | null;
  /** Orden de aparición de las píldoras en la landing. */
  sortOrder: number;
  /** BusinessProfile técnico (business-profile.ts) al que mapea este nicho comercial, para el CTA de trial/onboarding. */
  businessProfile: string | null;
  updatedAt: string;
}

export interface NichePlanMarketingRow {
  nicheId: string;
  planId: string;
  featureIds: string[];
  priceArs: number | null;
  limitsOverride: NichePlanLimitsOverride | null;
  planLabel: string | null;
  sortOrder: number;
  updatedAt: string;
}

/** Devuelve todas las filas de sm_niche_plan_marketing, opcionalmente filtrado por niche. */
export async function dbSmGetNichePlanMarketing(
  nicheId?: string,
): Promise<NichePlanMarketingRow[]> {
  const db = sql();
  const rows = nicheId
    ? await db`SELECT niche_id, plan_id, feature_ids, price_ars, limits_override, plan_label, sort_order, updated_at
               FROM sm_niche_plan_marketing WHERE niche_id = ${nicheId} ORDER BY sort_order`
    : await db`SELECT niche_id, plan_id, feature_ids, price_ars, limits_override, plan_label, sort_order, updated_at
               FROM sm_niche_plan_marketing ORDER BY niche_id, sort_order`;
  return rows.map((r) => ({
    nicheId: String(r.niche_id),
    planId: String(r.plan_id),
    featureIds: (r.feature_ids as string[]) ?? [],
    priceArs: r.price_ars != null ? Number(r.price_ars) : null,
    limitsOverride: (r.limits_override as NichePlanLimitsOverride | null) ?? null,
    planLabel: r.plan_label != null ? String(r.plan_label) : null,
    sortOrder: Number(r.sort_order),
    updatedAt: String(r.updated_at),
  }));
}

/** Upsert de feature_ids para un par (niche_id, plan_id). */
export async function dbSmUpsertNichePlanMarketing(
  nicheId: string,
  planId: string,
  featureIds: string[],
  priceArs?: number | null,
  limitsOverride?: NichePlanLimitsOverride | null,
  planLabel?: string | null,
): Promise<void> {
  const db = sql();
  const limitsJson = limitsOverride != null ? JSON.stringify(limitsOverride) : null;
  await db`
    INSERT INTO sm_niche_plan_marketing
      (niche_id, plan_id, feature_ids, price_ars, limits_override, plan_label, updated_at)
    VALUES (
      ${nicheId}, ${planId},
      ${featureIds as unknown as string},
      ${priceArs ?? null},
      ${limitsJson}::jsonb,
      ${planLabel ?? null},
      NOW()
    )
    ON CONFLICT (niche_id, plan_id) DO UPDATE SET
      feature_ids     = EXCLUDED.feature_ids,
      price_ars       = EXCLUDED.price_ars,
      limits_override = EXCLUDED.limits_override,
      plan_label      = EXCLUDED.plan_label,
      updated_at      = NOW()
  `;
}

export async function dbSmGetNicheConfigs(): Promise<NicheConfigRow[]> {
  const db = sql();
  const rows = await db`SELECT niche_id, enabled, discount_12m, discount_24m, discount_48m,
                                label, sort_order, business_profile, updated_at
                         FROM sm_niche_config ORDER BY sort_order, niche_id`;
  return rows.map((r) => ({
    nicheId:         String(r.niche_id),
    enabled:         Boolean(r.enabled),
    discount12m:     r.discount_12m != null ? Number(r.discount_12m) : null,
    discount24m:     r.discount_24m != null ? Number(r.discount_24m) : null,
    discount48m:     r.discount_48m != null ? Number(r.discount_48m) : null,
    label:           r.label != null ? String(r.label) : null,
    sortOrder:       Number(r.sort_order ?? 100),
    businessProfile: r.business_profile != null ? String(r.business_profile) : null,
    updatedAt:       String(r.updated_at),
  }));
}

export async function dbSmUpsertNicheConfig(
  nicheId: string,
  enabled: boolean,
  discount12m?: number | null,
  discount24m?: number | null,
  discount48m?: number | null,
  label?: string | null,
  sortOrder?: number | null,
  businessProfile?: string | null,
): Promise<void> {
  const db = sql();
  // COALESCE preserva label/sort_order/business_profile ya guardados cuando el caller
  // (ej. el toggle enabled/discuentos) no los pasa — evita que un PATCH parcial los borre.
  await db`
    INSERT INTO sm_niche_config (niche_id, enabled, discount_12m, discount_24m, discount_48m, label, sort_order, business_profile, updated_at)
    VALUES (${nicheId}, ${enabled}, ${discount12m ?? null}, ${discount24m ?? null}, ${discount48m ?? null}, ${label ?? null}, ${sortOrder ?? 100}, ${businessProfile ?? null}, NOW())
    ON CONFLICT (niche_id) DO UPDATE SET
      enabled          = EXCLUDED.enabled,
      discount_12m     = EXCLUDED.discount_12m,
      discount_24m     = EXCLUDED.discount_24m,
      discount_48m     = EXCLUDED.discount_48m,
      label            = COALESCE(${label ?? null}, sm_niche_config.label),
      sort_order       = CASE WHEN ${sortOrder ?? null}::int IS NULL THEN sm_niche_config.sort_order ELSE ${sortOrder ?? null} END,
      business_profile = COALESCE(${businessProfile ?? null}, sm_niche_config.business_profile),
      updated_at       = NOW()
  `;
}

// ─── FEATURE CONFIG ───────────────────────────────────────────────────────────

export interface FeatureConfigRow {
  featureId: string;
  label: string | null;
  showInLanding: boolean;
  updatedAt: string;
}

export async function dbSmGetFeatureConfigs(): Promise<FeatureConfigRow[]> {
  const db = sql();
  const rows = await db`SELECT feature_id, label, show_in_landing, updated_at FROM sm_feature_config`;
  return rows.map((r) => ({
    featureId:     String(r.feature_id),
    label:         r.label != null ? String(r.label) : null,
    showInLanding: Boolean(r.show_in_landing),
    updatedAt:     String(r.updated_at),
  }));
}

export async function dbSmUpsertFeatureConfig(
  featureId: string,
  label: string | null,
  showInLanding: boolean,
): Promise<void> {
  const db = sql();
  await db`
    INSERT INTO sm_feature_config (feature_id, label, show_in_landing, updated_at)
    VALUES (${featureId}, ${label}, ${showInLanding}, NOW())
    ON CONFLICT (feature_id) DO UPDATE SET
      label           = EXCLUDED.label,
      show_in_landing = EXCLUDED.show_in_landing,
      updated_at      = NOW()
  `;
}
